'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import * as d3 from 'd3';
import parse from 'html-react-parser';
import cytoscape from 'cytoscape';
// @ts-expect-error no type definition exists
import cise from 'cytoscape-cise';
import euler, {EulerLayoutOptions} from 'cytoscape-euler';

import {Button} from 'primereact/button';
import {Checkbox} from 'primereact/checkbox';
import {Dialog} from 'primereact/dialog';
import {InputNumber} from 'primereact/inputnumber';
import {MenuItem} from 'primereact/menuitem';
import {PanelMenu} from 'primereact/panelmenu';
import {Slider} from 'primereact/slider';
import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';
import {Message} from '~/shared/src/utilities/message';
import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import Item from '~/shared/src/ci/Item';
import Reference from '~/shared/src/ci/Reference';
import {
    LoadConfigParameters,
    LoadConfigResult,
    LoadDataParameters
} from '~/shared/src/utilities/modules/graph';
import {
    GraphType,
    Layout,
    SvgGraphTransform,
    SvgNodesTransform,
    Type
} from '~/shared/src/utilities/graph';
import {
    DataStructure,
    extractDataObjectsFromDataStructure
} from '~/shared/src/utilities/dataStructure';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import dataUtilities from '~/shared/src/utilities/dataUtils';

import {AllProps as FormAllProps} from '~/frontend/src/components/base/Form';
import xhr from '~/frontend/src/utilities/xhr';
import {
    getPanelProps,
    getSubPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    getIconSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import {getIntenseColorFromColorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import loadData from '~/frontend/src/actions/data/loadData';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';
import openPanelForItemType from '~/frontend/src/actions/panelManager/openPanelForItemType';


type NodeCommons = {
    id : number;
    svgId : string;
    label : string;
    icon : string;
    itemTypeId : number;
    color? : string;
};
type MetaNode = NodeCommons & {
    x : number;
    y : number;
    width : number;
    height : number;

    isRoot? : boolean;
    fixPositions : boolean;
    linkCount : number;
    uniqueLinkCount : number;
};
type DataNode = NodeCommons & {
    itemType : string;
    children? : DataNode[];
    parents? : number[];
    reference? : {
        referenceId : number;
        referenceType : string;
        referenceDirection : 'dest' | 'src';
    }[];
};
type Node = MetaNode | DataNode;

type LinkCommons = {
    id : number;
    svgId : string;
    label : string;
    fullLabel : string;
    source : number;
    sourceId : number;
    target : number;
    targetId : number;
};
type MetaLink = LinkCommons & {
    cardinality : string;
    sourceCardinality : '1' | 'N';
    targetCardinality : '1' | 'N' | 'M';
    num : number;
    reverse : boolean;
};
type DataLink = LinkCommons & {};
type Link = MetaLink | DataLink;

type LinkCounts = {
    [ key : string ] : number;
};

type Bundle = {
    id : string;
    parents : number[];
    level : number;
    span : number;
    links? : PositionedDataLink[];
    x : number;
    y : number;
};

type PositionedDataNode = DataNode & {
    level : number;
    x : number;
    y : number;
    width : number;
    height : number;
    bundle? : Bundle;
    bundles? : Bundle[];
};

type PositionedDataLink = {
    id : string;
    svgId : string;
    targetLabel : string;
    sourceLabel : string;
    fullLabel : string;
    source : number;
    target : number;
    bundle : Bundle;
};

type NodePositions = {
    [ nodeId : number ] : {
        x : number;
        y : number;
    };
};

export type DefaultProps = {
    schema? : Schema;

    graphType? : GraphType;
    layout? : Layout;
    type? : Type;

    colorItemTypes? : boolean;
    iconNodeOnly? : boolean;
    showLinkCardinality? : boolean;
    showLinkNames? : boolean;
    showTooltips? : boolean;

    showOptionsMenu? : boolean;
    showReferenceDirectionFilter? : boolean;
    showReferenceDepthFilter? : boolean;

    nodeLabel? : Message;
    nodesLabel? : Message;
    linkLabel? : Message;
    linksLabel? : Message;

    isGenericGraph? : boolean;

    dataDisplayConfig? : {
        referenceDirection : 'SrcToDest' | 'DestToSrc' | 'both';
        referenceDepth : number;
    };
    hiddenNodeItemTypes? : number[];
};
export const defaultProps : Required<DefaultProps> = {
    schema: 'DATA',

    graphType: 'force',
    layout   : 'auto',
    type     : 'meta',

    colorItemTypes     : true,
    iconNodeOnly       : true,
    showLinkCardinality: false,
    showLinkNames      : false,
    showTooltips       : true,

    showOptionsMenu             : true,
    showReferenceDirectionFilter: true,
    showReferenceDepthFilter    : true,

    nodeLabel : 'Node',
    nodesLabel: 'Nodes',
    linkLabel : 'Link',
    linksLabel: 'Links',

    isGenericGraph   : false,
    dataDisplayConfig: {
        referenceDirection: 'both',
        referenceDepth    : 1
    },

    hiddenNodeItemTypes: []
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    formUid? : string;

    /**
     * The key of the overview config.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    dataStructures? : DataStructure[];

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult[ 'data' ]} config
     */
    onConfigLoad? : ( config : LoadConfigResult[ 'data' ] ) => void;

    /**
     * Function invoked if data was successfully loaded.
     */
    onDataLoad? : () => void;

    onReloadView? : () => void;

    onGraphTypeChange? : ( graphType : GraphType ) => void;
    onShowTooltipsChange? : ( showTooltips : boolean ) => void;
    onColorItemTypesChange? : ( colorItemTypes : boolean ) => void;

    /**
     * Additional parameters which have to be relayed to the CFG:Query used to load the results.
     */
    additionalQueryParameters? : Required<LoadDataParameters[ 'parameters' ]>;

    dataConfig? : {
        schema : Schema;
    } & (
        {
            /**
             * The id of the root item. No further root item information required.
             */
            rootItemId : number;

            rootItemKey? : never;
            rootItemType? : never;
        } |
        {
            /**
             * The ItemType of the root item.
             */
            rootItemType : string;

            /**
             * The key of the root item. If set also rootItemType is required.
             */
            rootItemKey? : string;

            rootItemId? : never;
        }
        );

    svgGraphTransform? : SvgGraphTransform;
    svgNodesTransform? : SvgNodesTransform;
    selectedNodes? : number[];
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    configLoaded? : boolean;
    dataLoaded? : boolean;
    metaData? : PanelElusiveDataState[ 'metaData' ] & {
        rowData? : LoadMetaDataRowData[];
        rowDataMapping? : LoadMetaDataRowDataMapping;
    }
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;
const setFormParam = origSetParam as <K extends keyof FormAllProps>( panelKey : string, paramKey : K, paramValue : FormAllProps[K] ) => SetParamAction;


type SimulationParameters = {
    simulationStartAlpha : number;
    alphaDecay : number;
    velocityDecay : number;
    manyBody : boolean;
    manyBodyStrength : number;
    manyBodyTheta : number;
    manyBodyDistanceMin : number;
    manyBodyDistanceMax? : number;
    linkForce : boolean;
    linkDistance : number;
    linkStrength : number;
};

const debug                                                                                = ENVIRONMENT !== 'production',
      showSimulationParametersInOptionsMenu                                                = true,
      simulationParametersPresets : { [ referenceDepth : string ] : SimulationParameters } = {
          '1': {
              simulationStartAlpha: .6,
              alphaDecay          : .08,
              velocityDecay       : .5,
              manyBody            : true,
              manyBodyStrength    : -50,
              manyBodyTheta       : .9,
              manyBodyDistanceMin : 1,
              manyBodyDistanceMax : undefined,
              linkForce           : true,
              linkDistance        : 200,
              linkStrength        : 2
          },
          '2': {
              simulationStartAlpha: 1,
              alphaDecay          : .04,
              velocityDecay       : .6,
              manyBody            : true,
              manyBodyStrength    : -22,
              manyBodyTheta       : .7,
              manyBodyDistanceMin : .6,
              manyBodyDistanceMax : undefined,
              linkForce           : true,
              linkDistance        : 110,
              linkStrength        : 1.6
          },
          '3': {
              simulationStartAlpha: 1,
              alphaDecay          : .1,
              velocityDecay       : .5,
              manyBody            : true,
              manyBodyStrength    : -35,
              manyBodyTheta       : .9,
              manyBodyDistanceMin : 4,
              manyBodyDistanceMax : undefined,
              linkForce           : true,
              linkDistance        : 110,
              linkStrength        : 2
          },
          '4': {
              simulationStartAlpha: .6,
              alphaDecay          : .08,
              velocityDecay       : .5,
              manyBody            : true,
              manyBodyStrength    : -50,
              manyBodyTheta       : .9,
              manyBodyDistanceMin : 1,
              manyBodyDistanceMax : undefined,
              linkForce           : true,
              linkDistance        : 200,
              linkStrength        : 2
          }
      };


const Graph = ( props : AllProps ) => {
    const dispatch : AppDispatch          = useDispatch(),
          subPanelProps                   = useSelector( ( state : StoreState ) => {
              return getSubPanelProps( props.uid, state );
          } ),
          formPersistentSubPanelPropsOrig = useSelector( ( state : StoreState ) => {
              if ( props.formUid ) {
                  return ( getPanelProps( props.formUid, state )?.panelProps ?? getSubPanelProps( props.formUid, state ) )?.persistentSubPanelProps;
              }
              else {
                  return undefined;
              }
          } ),
          formPersistentSubPanelProps     = useRef<any | undefined>( undefined );

    formPersistentSubPanelProps.current = formPersistentSubPanelPropsOrig;

    const setFormPersistentSubPanelProps = useCallback(
        <K extends keyof AllProps>( paramKey : K, paramValue : AllProps[K] ) => {
            if ( props.formUid && formPersistentSubPanelProps.current?.[ props.uid ]?.[ paramKey ] !== paramValue ) {
                formPersistentSubPanelProps.current = {
                    ...formPersistentSubPanelProps.current,
                    [ props.uid ]: {
                        ...formPersistentSubPanelProps.current?.[ props.uid ],
                        [ paramKey ]: paramValue
                    }
                };

                dispatch( setFormParam(
                    props.formUid,
                    'persistentSubPanelProps',
                    formPersistentSubPanelProps.current
                ) );
            }
        },
        [
            props.formUid,
            props.uid
        ]
    );

    props = useMemo(
        () => {
            return {
                ...props,
                ...subPanelProps,
                ...formPersistentSubPanelPropsOrig?.[ props.uid ]
            };
        },
        [
            props,
            subPanelProps,
            formPersistentSubPanelPropsOrig?.[ props.uid ]
        ]
    );

    const {
              additionalQueryParameters,
              colorItemTypes,
              dataConfig,
              dataDisplayConfig,
              dataStructures,
              graphType,
              hiddenNodeItemTypes,
              iconNodeOnly,
              isGenericGraph,
              layout,
              linkLabel,
              linksLabel,
              nodeLabel,
              nodesLabel,
              onColorItemTypesChange,
              onConfigLoad,
              onDataLoad,
              onGraphTypeChange,
              onReloadView,
              onShowTooltipsChange,
              schema,
              selectedNodes,
              showLinkCardinality,
              showLinkNames,
              showOptionsMenu,
              showReferenceDepthFilter,
              showReferenceDirectionFilter,
              showTooltips,
              svgGraphTransform,
              svgNodesTransform,
              type,
              uid,
              viewKey
          }                                                         = props,
          dataContext                                               = `${props.dataContext}_graph`,
          elementId                                                 = `${uid}_graph`,
          nodesId                                                   = `${elementId}_nodes`,
          linksId                                                   = `${elementId}_links`,
          zoomTargetElementId                                       = `${elementId}_zoom_target`,
          backgroundElementId                                       = `${elementId}_background`,
          arrowMarkerId                                             = `${elementId}_marker_arrow`,
          shadowFilterId                                            = `${elementId}_filter_shadow`,
          cytoscopeContainerid                                      = `${elementId}_cytoscape_container`,
          user                                                      = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                                   = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          elusiveData                                               = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          dataElusiveData                                           = useSelector( ( state : StoreState ) => state.elusiveData?.[ dataContext ] as ElusiveData | undefined ),
          isLoading                                                 = useRef<boolean>( false ),
          hasRendered                                               = useRef<boolean>( false ),
          justDblClicked                                            = useRef<boolean>( false ),
          userZoom                                                  = useRef<boolean>( false ),
          userDrag                                                  = useRef<boolean>( false ),
          graphSvg                                                  = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>( null ),
          svgSimulation                                             = useRef<d3.Simulation<MetaNode, MetaLink> | null>( null ),
          linkCounts                                                = useRef<LinkCounts>( {} ),
          cNodes                                                    = useRef<Node[] | Node[][]>( [] ),
          cFlatNodes                                                = useRef<Node[]>( [] ),
          cLinks                                                    = useRef<Link[]>( [] ),
          cSelectedNodes                                            = useRef<number[]>( selectedNodes ?? [] ),
          setSvgNodesTransformTimeoutId                             = useRef<NodeJS.Timeout | undefined>(),
          setSvgGraphTransformTimeoutId                             = useRef<NodeJS.Timeout | undefined>(),
          observedGraphParentElement                                = useRef<Element | undefined>( undefined ),
          cRenderToggle                                             = useRef<number>( 0 ),
          cGraphDimensions                                          = useRef<{
              width : number;
              height : number;
          }>( { width: 0, height: 0 } ),
          cZoom                                                     = useRef<d3.ZoomBehavior<SVGGElement, unknown> | undefined>( undefined ),
          resizeTimeoutId                                           = useRef<NodeJS.Timeout | undefined>(),
          nodeClickTimeoutId                                        = useRef<NodeJS.Timeout | undefined>(),
          itemTypeColorMapping                                      = useRef<{ [ itemType : number ] : string }>( {} ),
          [ graphHeight, setGraphHeight ]                           = useState<number>( 0 ),
          [ graphWidth, setGraphWidth ]                             = useState<number>( 0 ),
          [ rerenderToggle, setRerenderToggle ]                     = useState<number>( 0 ),
          [ filtersMenuExpandedKeys, setFiltersMenuExpandedKeys ]   = useState<any>( {} ),
          [ optionMenuExpandedKeys, setOptionMenuExpandedKeys ]     = useState<any>( {} ),
          [ tooltipContent, setTooltipContent ]                     = useState<React.ReactNode>( '' ),
          [ tooltipTarget, setTooltipTarget ]                       = useState<string>( '' ),
          [ resetViewToggle, setResetViewToggle ]                   = useState<number>( 0 ),
          [ fullScreen, setFullScreen ]                             = useState<boolean>( false ),
          [ usePresets, setUsePresets ]                             = useState<boolean>( true ),
          [ showSimulationParameters, setShowSimulationParameters ] = useState<boolean>( false ),
          [ simulationParameters, setSimulationParameters ]         = useState<SimulationParameters>( {
              simulationStartAlpha: .6,
              alphaDecay          : .08,
              velocityDecay       : .5,
              manyBody            : true,
              manyBodyStrength    : -50,
              manyBodyTheta       : .9,
              manyBodyDistanceMin : 1,
              manyBodyDistanceMax : undefined,
              linkForce           : true,
              linkDistance        : 200,
              linkStrength        : 2
          } ),
          configRequestUid                                          = 'loadConfig',
          dataRequestUid                                            = 'loadData',
          isConfigLoaded                                            = useMemo(
              () => {
                  return elusiveData?.configLoaded ?? false;
              },
              [
                  elusiveData?.configLoaded ?? false
              ]
          ),
          isDataLoaded                                              = useMemo(
              () => {
                  return type === 'meta' || elusiveData?.dataLoaded;
              },
              [
                  elusiveData?.dataLoaded ?? false,
                  type
              ]
          ),
          language                                                  = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                                        = useMemo(
              () => {
                  return JSON.stringify( dataElusiveData?.data ?? {} );
              },
              [
                  dataElusiveData?.data
              ]
          ),
          dataSchema                                                = useMemo(
              () => {
                  return ( dataStructures?.[ 0 ]?.item?.objectSchema?.toUpperCase() ?? schema ?? 'DATA' ) as Schema;
              },
              [
                  dataStructures,
                  schema
              ]
          ),
          graphElement                                              = document.getElementById( elementId ),
          graphParentElement                                        = useMemo(
              () => graphElement?.parentElement,
              [
                  graphElement
              ]
          ),
          setGenericGraphConfig                                     = useCallback(
              () => {
                  // @ts-expect-error all necessary props will be set if they are present
                  const dataConfigToSet : Required<ComponentProps>['dataConfig'] = {
                      schema: ( additionalQueryParameters?.schema as Schema | undefined ) ?? schema
                  };

                  let dataToSet : Partial<AllProps> = {};

                  if ( additionalQueryParameters?.rootItemId ) {
                      dataConfigToSet.rootItemId = Number( additionalQueryParameters.rootItemId );
                  }
                  else if ( additionalQueryParameters?.rootItemType ) {
                      dataConfigToSet.rootItemType = additionalQueryParameters.rootItemType;
                      dataConfigToSet.rootItemKey = additionalQueryParameters?.rootItemKey;
                  }

                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  if ( dataConfigToSet.rootItemId || dataConfigToSet.rootItemType ) {
                      dataToSet = {
                          ...dataToSet,
                          dataConfig: dataConfigToSet,
                          nodeLabel : 'YN.base.frontend.graph.data.label.node',
                          nodesLabel: 'YN.base.frontend.graph.data.label.nodes',
                          linkLabel : 'YN.base.frontend.graph.data.label.link',
                          linksLabel: 'YN.base.frontend.graph.data.label.links',

                          graphType: 'tree'
                      };
                  }

                  dispatch( setParams( uid, dataToSet ) );
                  dispatch( setElusiveData( uid, { configLoaded: true } ) );
              },
              [
                  additionalQueryParameters,
                  schema
              ]
          ),
          loadConfig                                                = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  const params : LoadConfigParameters = {
                            viewKey,
                            parameters: additionalQueryParameters
                        },
                        response                      = await xhr.post(
                            `${BASE_URL}/api/graph/loadConfig`,
                            params,
                            undefined,
                            uid,
                            configRequestUid
                        );

                  if ( response?.data?.success ) {
                      const result = response.data as LoadConfigResult,
                            config = result.data;

                      let dataToSet : Partial<AllProps> = {
                          configHash: config.configHash,
                          schema    : config.schema,
                          graphType : config.graphType,
                          type      : config.type,
                          layout    : config.layout,

                          colorItemTypes     : config.colorItemTypes ?? colorItemTypes,
                          iconNodeOnly       : config.iconNodeOnly ?? iconNodeOnly,
                          showLinkCardinality: config.showLinkCardinality ?? showLinkCardinality,
                          showLinkNames      : config.showLinkNames ?? showLinkNames,
                          showTooltips       : config.showTooltips ?? showTooltips,

                          svgGraphTransform: config.svgGraphTransform ?? svgGraphTransform,
                          svgNodesTransform: config.svgNodesTransform ?? svgNodesTransform
                      };

                      if ( dataToSet.type === 'meta' ) {
                          dataToSet = {
                              ...dataToSet,
                              dataStructures: config.dataStructures,
                              nodeLabel     : config.nodeLabel ?? nodeLabel,
                              nodesLabel    : config.nodesLabel ?? nodesLabel,
                              linkLabel     : config.linkLabel ?? linkLabel,
                              linksLabel    : config.linksLabel ?? linksLabel
                          };
                      }
                      else {
                          // @ts-expect-error all necessary props will be set if they are present
                          const dataConfigToSet : Required<ComponentProps>['dataConfig'] = {
                              schema: ( additionalQueryParameters?.schema as Schema | undefined ) ?? config.schema
                          };

                          if ( additionalQueryParameters?.rootItemId ) {
                              dataConfigToSet.rootItemId = Number( additionalQueryParameters.rootItemId );
                          }
                          else if ( additionalQueryParameters?.rootItemType ) {
                              dataConfigToSet.rootItemType = additionalQueryParameters.rootItemType;
                              dataConfigToSet.rootItemKey = additionalQueryParameters?.rootItemKey;
                          }

                          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                          if ( dataConfigToSet.rootItemId || dataConfigToSet.rootItemType ) {
                              dataToSet = {
                                  ...dataToSet,
                                  dataConfig: dataConfigToSet,
                                  nodeLabel : config.nodeLabel ?? 'YN.base.frontend.graph.data.label.node',
                                  nodesLabel: config.nodesLabel ?? 'YN.base.frontend.graph.data.label.nodes',
                                  linkLabel : config.linkLabel ?? 'YN.base.frontend.graph.data.label.link',
                                  linksLabel: config.linksLabel ?? 'YN.base.frontend.graph.data.label.links'
                              };
                          }
                      }

                      dispatch( setParams( uid, dataToSet ) );
                      dispatch( setElusiveData( uid, { configLoaded: true } ) );

                      onConfigLoad?.( config );
                  }

                  dispatch( endRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  isLoading.current = false;
              },
              [
                  additionalQueryParameters,
                  uid,
                  viewKey
              ]
          ),
          load                                                      = useCallback(
              async () => {
                  if ( dataConfig ) {
                      isLoading.current = true;

                      dispatch( startRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      if ( global.data[ dataContext ] ) {
                          delete global.data[ dataContext ];
                      }

                      const params : LoadDataParameters = {
                                ...dataConfig,
                                ...dataDisplayConfig
                            },
                            response                    = await dispatch(
                                loadData(
                                    'graph/loadData',
                                    dataContext,
                                    uid,
                                    dataRequestUid,
                                    dataSchema,
                                    {
                                        params,
                                        mergeMode        : 'override',
                                        extraMetaDataKeys: [ 'rowData', 'rowDataMapping' ]
                                    }
                                )
                            );

                      if ( response ) {
                          onDataLoad?.();
                      }

                      dispatch( setElusiveData( uid, { dataLoaded: true } ) );

                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  dataContext,
                  dataConfig,
                  dataDisplayConfig,
                  uid
              ]
          ),
          nodeItemTypes                                             = useMemo(
              () => {
                  const retVal : ItemType[] = [];

                  itemTypeColorMapping.current = {};

                  if ( type === 'data' ) {
                      for ( const item of Object.values( global.data[ dataContext ]?.items ?? {} ) ) {
                          if ( !retVal.includes( item.itemType ) ) {
                              retVal.push( item.itemType );
                          }

                          if ( !itemTypeColorMapping.current[ item.itemTypeId ] ) {
                              itemTypeColorMapping.current[ item.itemTypeId ] = `${getIntenseColorFromColorPalette( Object.keys( itemTypeColorMapping.current ).length )}40`;
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  type
              ]
          ),
          rootItems                                                 = useMemo(
              () => {
                  const retVal : Item[] = [];

                  if ( global.data[ dataContext ] && dataConfig ) {
                      // get root(s)
                      if ( dataConfig.rootItemId ) {
                          const item = global.data[ dataContext ].items[ dataConfig.rootItemId ];

                          if ( item ) {
                              retVal.push( item );
                          }
                      }
                      else if ( dataConfig.rootItemType ) {
                          if ( dataConfig.rootItemKey ) {
                              const item = dataUtilities.findItem( dataContext, dataConfig.rootItemType, dataConfig.rootItemKey );

                              if ( item ) {
                                  retVal.push( item );
                              }
                          }
                          else {
                              const items = dataUtilities.findItems( dataContext, dataConfig.rootItemType );

                              if ( items ) {
                                  retVal.push( ...items );
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataConfig,
                  dataContext,
                  globalDataVersions
              ]
          ),
          items                                                     = useMemo(
              () => {
                  const retVal : Item[]       = [],
                        handledItems : Item[] = [],
                        getReferencedItems    = ( item : Item ) => {
                            const innerRetVal : Item[] = [],
                                  itemReferences       = item.references ?? [];

                            for ( const referenceId of itemReferences ) {
                                const reference = global.data[ dataContext ].references[ referenceId ];

                                if ( reference ) {
                                    if (
                                        reference.srcItemId === item.id
                                        && !hiddenNodeItemTypes.includes( reference.referenceSet.destItemType.id )
                                        && reference.destItem
                                        && !innerRetVal.includes( reference.destItem )
                                        && !handledItems.includes( reference.destItem )
                                    ) {
                                        handledItems.push( reference.destItem );

                                        innerRetVal.push( reference.destItem );
                                        innerRetVal.push( ...getReferencedItems( reference.destItem ) );
                                    }
                                    else if (
                                        reference.destItemId === item.id
                                        && !hiddenNodeItemTypes.includes( reference.referenceSet.srcItemType.id )
                                        && reference.srcItem
                                        && !innerRetVal.includes( reference.srcItem )
                                        && !handledItems.includes( reference.srcItem )
                                    ) {
                                        handledItems.push( reference.srcItem );

                                        innerRetVal.push( reference.srcItem );
                                        innerRetVal.push( ...getReferencedItems( reference.srcItem ) );
                                    }
                                }
                            }

                            return innerRetVal;
                        };

                  for ( const item of rootItems ) {
                      handledItems.push( item );

                      retVal.push( item );
                      retVal.push( ...getReferencedItems( item ) );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalDataVersions,
                  hiddenNodeItemTypes,
                  rootItems
              ]
          ),
          references                                                = useMemo(
              () => {
                  const retVal : Reference[]            = [],
                        handledItems : Item[]           = [],
                        handledReferences : Reference[] = [],
                        getReferencedReferences         = ( item : Item ) => {
                            const innerRetVal : Reference[] = [];

                            for ( const referenceId of item.references ) {
                                const reference = global.data[ dataContext ].references[ referenceId ];

                                if ( reference ) {
                                    if (
                                        reference.srcItemId === item.id
                                        && !hiddenNodeItemTypes.includes( reference.referenceSet.destItemType.id )
                                        && reference.destItem
                                        && !innerRetVal.includes( reference )
                                        && !handledReferences.includes( reference )
                                    ) {
                                        handledReferences.push( reference );

                                        innerRetVal.push( reference );

                                        if (
                                            !handledItems.includes( reference.destItem )
                                        ) {
                                            handledItems.push( reference.destItem );
                                            innerRetVal.push( ...getReferencedReferences( reference.destItem ) );
                                        }
                                    }
                                    else if (
                                        reference.destItemId === item.id
                                        && !hiddenNodeItemTypes.includes( reference.referenceSet.srcItemType.id )
                                        && reference.srcItem
                                        && !innerRetVal.includes( reference )
                                        && !handledReferences.includes( reference )
                                    ) {
                                        handledReferences.push( reference );

                                        innerRetVal.push( reference );

                                        if (
                                            !handledItems.includes( reference.srcItem )
                                        ) {
                                            handledItems.push( reference.srcItem );
                                            innerRetVal.push( ...getReferencedReferences( reference.srcItem ) );
                                        }
                                    }
                                }
                            }

                            return innerRetVal;
                        };

                  for ( const item of rootItems ) {
                      handledItems.push( item );

                      retVal.push( ...getReferencedReferences( item ) );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalDataVersions,
                  hiddenNodeItemTypes,
                  rootItems
              ]
          ),
          getCytoscapeContainer                                     = () => {
              let retVal = document.getElementById( cytoscopeContainerid );

              if ( retVal ) {
                  document.body.removeChild( retVal );
              }

              retVal = document.createElement( 'div' );

              retVal.id = cytoscopeContainerid;
              retVal.style.width = '100%';
              retVal.style.height = '100%';
              retVal.style.overflow = 'hidden';
              retVal.style.position = 'absolute';
              retVal.style.top = '0px';
              retVal.style.left = '0px';
              retVal.style.width = `100vw`;
              retVal.style.height = `100vh`;

              document.body.appendChild( retVal );

              return retVal;
          },
          removeCytoscapeContainer                                  = () => {
              const container = document.getElementById( cytoscopeContainerid );

              if ( container ) {
                  document.body.removeChild( container );
              }
          },
          getCytoscopeNodeElements                                  = ( nodes : Node[] ) => {
              return nodes.map( n => {
                  return {
                      group   : 'nodes',
                      data    : {
                          ...n,
                          id: n.svgId
                      } as cytoscape.NodeDataDefinition,
                      position: {
                          x: 0,
                          y: 0
                      }
                  };
              } ) as cytoscape.ElementDefinition[];
          },
          getCytoscopeLinkElements                                  = ( links : Link[] ) => {
              return links.map( l => {
                  return {
                      group: 'edges',
                      data : {
                          id    : l.svgId,
                          source: `node_${l.sourceId}`,
                          target: `node_${l.targetId}`
                      } as cytoscape.EdgeDataDefinition
                  };
              } ) as cytoscape.ElementDefinition[];
          },
          applyCytoscopePositions                                   = ( nodes : MetaNode[], nodeElements : cytoscape.ElementDefinition[] ) : NodePositions => {
              const retVal : NodePositions = {};

              for ( let i = 0; i < nodes.length; i++ ) {
                  const nodeElement = nodeElements.find( ne => ne.data.id === nodes[ i ].svgId );

                  nodes[ i ].x = nodeElement?.position?.x ?? 0;
                  nodes[ i ].y = nodeElement?.position?.y ?? 0;

                  retVal[ nodes[ i ].id ] = {
                      x: nodes[ i ].x,
                      y: nodes[ i ].y
                  };
              }

              return retVal;
          },
          countLinkIntersections                                    = ( nodePositions : NodePositions, links : Link[] ) => {
              const startTime  = performance.now(),
                    intersects = ( sX1 : number, sY1 : number, eX1 : number, eY1 : number, sX2 : number, sY2 : number, eX2 : number, eY2 : number ) => {
                        const det = ( eX1 - sX1 ) * ( eY2 - sY2 ) - ( eX2 - sX2 ) * ( eY1 - sY1 );

                        let retVal = false,
                            gamma : number,
                            lambda : number;

                        if ( det !== 0 ) {
                            lambda = ( ( eY2 - sY2 ) * ( eX2 - sX1 ) + ( sX2 - eX2 ) * ( eY2 - sY1 ) ) / det;
                            gamma = ( ( sY1 - eY1 ) * ( eX2 - sX1 ) + ( eX1 - sX1 ) * ( eY2 - sY1 ) ) / det;
                            retVal = ( 0 < lambda && lambda < 1 ) && ( 0 < gamma && gamma < 1 );
                        }

                        return retVal;
                    };

              let retVal = 0;

              for ( let i = 0; i < links.length - 1; i++ ) {
                  for ( let j = i + 1; j < links.length; j++ ) {
                      if (
                          !(
                              links[ i ].sourceId === links[ j ].sourceId
                              && links[ i ].targetId === links[ j ].targetId
                          )
                          && !(
                              links[ i ].sourceId === links[ j ].targetId
                              && links[ i ].targetId === links[ j ].sourceId
                          )
                      ) {
                          if ( intersects(
                              nodePositions[ links[ i ].sourceId ].x,
                              nodePositions[ links[ i ].sourceId ].y,
                              nodePositions[ links[ i ].targetId ].x,
                              nodePositions[ links[ i ].targetId ].y,
                              nodePositions[ links[ j ].sourceId ].x,
                              nodePositions[ links[ j ].sourceId ].y,
                              nodePositions[ links[ j ].targetId ].x,
                              nodePositions[ links[ j ].targetId ].y
                          ) ) {
                              retVal++;
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `link intersections - ${retVal} - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms`.asLogLine( 100 ) );
              }

              return retVal;
          },
          ciseLayout                                                = ( nodes : MetaNode[], links : Link[] ) => {
              if ( nodes.length ) {
                  cytoscape.use( cise );
                  const nodeElements                      = getCytoscopeNodeElements( nodes ),
                        cy                                = cytoscape( {
                            container: getCytoscapeContainer(),
                            elements : nodeElements.concat( getCytoscopeLinkElements( links ) ),
                            layout   : { name: 'circle' },
                            style    : [
                                {
                                    selector: 'node',
                                    style   : {
                                        width : type === 'meta' ? 200 : 50,
                                        height: 50
                                    }
                                }
                            ]
                        } ),
                        clusters                          = cy.elements().markovClustering( {
                            // A number that affects time of computation and cluster granularity to some extent: M * M
                            expandFactor: 2, // 2

                            // A number that affects cluster granularity (the greater the value, the more clusters): M(i,j) / E(j)
                            inflateFactor: 2, // 2

                            // Optional number of self loops for each node. Use a neutral value to improve cluster computations
                            multFactor: 3, // 1

                            // Maximum number of iterations of the MCL algorithm in a single run
                            maxIterations: 20 // 20
                        } ),
                        arrayOfClusterArrays : string[][] = clusters.map(
                            ( cluster, clusterIndex ) => {
                                return cluster.nodes().map(
                                    node => {
                                        node.data( 'clusterID', clusterIndex );

                                        return node.data().id;
                                    }
                                );
                            }
                        ),
                        cyLayout                          = cy.layout( {
                            name    : 'cise',
                            clusters: arrayOfClusterArrays,


                            // separation amount between nodes in a cluster
                            // note: increasing this amount will also increase the simulation time
                            nodeSeparation: 12.5, // 12.5

                            // Inter-cluster edge length factor
                            // (2.0 means inter-cluster edges should be twice as long as intra-cluster edges)
                            idealInterClusterEdgeLengthCoefficient: 14, // 1.4

                            // Whether to pull on-circle nodes inside of the circle
                            allowNodesInsideCircle: false, // false

                            // Max percentage of the nodes in a circle that can move inside the circle
                            maxRatioOfNodesInsideCircle: 0.1, // 0.1

                            // - Lower values give looser springs
                            // - Higher values give tighter springs
                            springCoeff: ( edge : cytoscape.EdgeSingular ) => 0.45, // 0.45

                            // Node repulsion (non overlapping) multiplier
                            nodeRepulsion: ( node : cytoscape.NodeSingular ) => 4500, // 4500

                            // Gravity force (constant)
                            gravity: 0.25, // 0.25

                            // Gravity range (constant)
                            gravityRange: 3.8, // 3.8


                            // Use random node positions at beginning of layout
                            // if this is set to false, the layout will be incremental
                            randomize: true, // true

                            // Whether to animate the layout
                            // - true : Animate while the layout is running
                            // - false : Just show the end result
                            // - 'end' : Animate directly to the end result
                            animate: false, // true

                            // number of ticks per frame; higher is faster but more jerky
                            refresh: 10, // 10

                            // Animation duration used for animate:'end'
                            animationDuration: undefined, // undefined

                            // Easing for animate:'end'
                            animationEasing: undefined, // undefined

                            // Whether to fit the viewport to the repositioned graph
                            // true : Fits at end of layout for animate:false or animate:'end'
                            fit: true, // true

                            // Padding in rendered co-ordinates around the layout
                            padding: 30, // 30

                            // Constrain layout bounds with one of
                            // - { x1, y1, x2, y2 }
                            // - { x1, y1, w, h }
                            // - undefined / null : Unconstrained
                            boundingBox: graphWidth ? { x1: 0, y1: 0, x2: graphWidth, y2: graphHeight } : undefined, // undefined

                            // Whether to include labels in node dimensions
                            nodeDimensionsIncludeLabels: true, // false

                            // whether to pack components of the graph, if set to true, you should import cytoscape.js-layout-utilities
                            packComponents: false,// false


                            // Called on `layoutready`
                            ready: function() {
                            },
                            // Called on `layoutstop`
                            stop: function() {
                            }
                        } as unknown as cytoscape.LayoutOptions );

                  cyLayout.run();

                  // apply computed positions to the nodes
                  const nodePositions = applyCytoscopePositions( nodes, nodeElements );

                  if ( debug ) {
                      countLinkIntersections( nodePositions, links );
                  }

                  removeCytoscapeContainer();
              }
          },
          /**
           * Euler layout will cause memory crash if at least one node not connected to the graph
           */
          eulerLayout                                               = ( nodes : MetaNode[], links : Link[] ) => {
              if ( nodes.length ) {
                  const nodeElements = getCytoscopeNodeElements( nodes );

                  cytoscape.use( euler );
                  cytoscape( {
                      headless: true,
                      elements: nodeElements.concat( getCytoscopeLinkElements( links ) ),
                      style   : [
                          {
                              selector: 'node',
                              style   : {
                                  width : type === 'meta' ? 200 : 50,
                                  height: 50
                              }
                          }
                      ],
                      layout  : {
                          name: 'euler',

                          // The ideal length of a spring
                          // - This acts as a hint for the edge length
                          // - The edge length can be longer or shorter if the forces are set to extreme values
                          springLength: 140, // ( edge:cytoscape.EdgeSingular ) => 80

                          // Hooke's law coefficient
                          // - The value ranges on [0, 1]
                          // - Lower values give looser springs
                          // - Higher values give tighter springs
                          springCoeff: 0.0008, // ( edge:cytoscape.EdgeSingular ) => 0.0008,

                          // The mass of the node in the physics simulation
                          // - The mass affects the gravity node repulsion/attraction
                          mass: ( node : cytoscape.NodeSingular ) => {
                              const rNode = nodes.find( n => n.svgId === node.data().id );

                              return Math.pow( ( rNode?.uniqueLinkCount ?? 1 ), 2.4 );
                          }, // 4

                          // Coulomb's law coefficient
                          // - Makes the nodes repel each other for negative values
                          // - Makes the nodes attract each other for positive values
                          gravity: -1.2, // -1.2

                          // A force that pulls nodes towards the origin (0, 0)
                          // Higher values keep the components less spread out
                          pull: 0.005, // 0.001

                          // Theta coefficient from Barnes-Hut simulation
                          // - Value ranges on [0, 1]
                          // - Performance is better with smaller values
                          // - Very small values may not create enough force to give a good result
                          theta: 0.666, // 0.666

                          // Friction / drag coefficient to make the system stabilise over time
                          dragCoeff: 0.02, // 0.02

                          // When the total of the squared position deltas is less than this value, the simulation ends
                          movementThreshold: 1, // 1

                          // The amount of time passed per tick
                          // - Larger values result in faster runtimes but might spread things out too far
                          // - Smaller values produce more accurate results
                          timeStep: 20, // 20

                          // Maximum iterations and time (in ms) before the layout will bail out
                          // - A large value may allow for a better result
                          // - A small value may make the layout end prematurely
                          // - The layout may stop before this if it has settled
                          maxIterations    : 1000, // 1000
                          maxSimulationTime: 4000, // 4000


                          // Whether to randomize the initial positions of the nodes
                          // true : Use random positions within the bounding box
                          // false : Use the current node positions as the initial positions
                          randomize: true, // false

                          // Whether to animate the layout
                          // - true : Animate while the layout is running
                          // - false : Just show the end result
                          // - 'end' : Animate directly to the end result
                          animate: false, // true

                          // The number of ticks per frame for animate:true
                          // - A larger value reduces rendering cost but can be jerky
                          // - A smaller value increases rendering cost but is smoother
                          refresh: 10, // 10

                          // Animation duration used for animate:'end'
                          animationDuration: undefined, // undefined

                          // Easing for animate:'end'
                          animationEasing: undefined, // undefined

                          // Prevent the user grabbing nodes during the layout (usually with animate:true)
                          ungrabifyWhileSimulating: false, // false

                          // Whether to fit the viewport to the repositioned graph
                          // true : Fits at end of layout for animate:false or animate:'end'; fits on each frame for animate:true
                          fit: true, // true

                          // Padding in rendered co-ordinates around the layout
                          padding: 30, // 30

                          // Constrain layout bounds with one of
                          // - { x1, y1, x2, y2 }
                          // - { x1, y1, w, h }
                          // - undefined / null : Unconstrained
                          boundingBox: graphWidth ? { x1: 0, y1: 0, x2: graphWidth, y2: graphHeight } : undefined, // undefined

                          // Whether to include labels in node dimensions
                          nodeDimensionsIncludeLabels: true, // false

                          // whether to pack components of the graph, if set to true, you should import cytoscape.js-layout-utilities
                          packComponents: false,// false


                          // Called on `layoutready`
                          ready: function() {
                          },
                          // Called on `layoutstop`
                          stop: function() {
                          }
                      } as unknown as EulerLayoutOptions
                  } );

                  // apply computed positions to the nodes
                  const nodePositions = applyCytoscopePositions( nodes, nodeElements );

                  if ( debug ) {
                      countLinkIntersections( nodePositions, links );
                  }
              }
          },
          links                                                     = useMemo(
              () => {
                  const retVal : Link[] = [];

                  linkCounts.current = {};

                  if ( type === 'meta' && dataStructures ) {
                      const getReferenceSets = ( subDataStructure : DataStructure ) => {
                                const innerRetVal : ReferenceSet[] = [],
                                      srcItemType                  = subDataStructure.item.objectType;

                                if ( srcItemType ) {
                                    for ( const subSubDataStructure of subDataStructure.dataStructure ?? [] ) {
                                        const targetItemType = subSubDataStructure.item.objectType;

                                        if ( targetItemType && subSubDataStructure.reference?.referenceConfiguration ) {
                                            for ( const referenceTypeName of Object.keys( subSubDataStructure.reference.referenceConfiguration ) ) {
                                                const referenceSet = metaModel[ ( subSubDataStructure.item.objectSchema ?? schema ) as Schema ].getReferenceSetsForItemTypesAndReferenceType(
                                                    srcItemType,
                                                    targetItemType,
                                                    referenceTypeName,
                                                    subSubDataStructure.reference.referenceConfiguration[ referenceTypeName ].referenceDirection === 'SrcToDest'
                                                        ? 'src'
                                                        : 'dest'
                                                )?.[ 0 ];

                                                if ( referenceSet && !innerRetVal.includes( referenceSet ) ) {
                                                    innerRetVal.push( referenceSet );
                                                }
                                            }
                                        }

                                        const subReferenceSets = getReferenceSets( subSubDataStructure );

                                        for ( const referenceSet of subReferenceSets ) {
                                            if ( !innerRetVal.includes( referenceSet ) ) {
                                                innerRetVal.push( referenceSet );
                                            }
                                        }
                                    }
                                }

                                return innerRetVal;
                            },
                            referenceSets    = dataStructures.reduce(
                                ( acc, dataStructure ) => {
                                    const innerReferenceSets = getReferenceSets( dataStructure );

                                    for ( const referenceSet of innerReferenceSets ) {
                                        if ( !acc.includes( referenceSet ) ) {
                                            acc.push( referenceSet );
                                        }
                                    }

                                    return acc;
                                },
                                [] as ReferenceSet[]
                            );

                      for ( const referenceSet of referenceSets ) {
                          const referenceSetCountKey = Math.min( referenceSet.srcItemTypeId, referenceSet.destItemTypeId ) + '_' + Math.max( referenceSet.srcItemTypeId, referenceSet.destItemTypeId );

                          if ( !linkCounts.current[ referenceSetCountKey ] ) {
                              linkCounts.current[ referenceSetCountKey ] = 0;
                          }

                          linkCounts.current[ referenceSetCountKey ]++;

                          retVal.push( {
                              id               : referenceSet.id,
                              svgId            : `link_${referenceSet.id}`,
                              label            : referenceSet.referenceTypeLabel,
                              fullLabel        : referenceSet.label,
                              cardinality      : referenceSet.cardinality,
                              source           : referenceSet.srcItemTypeId,
                              sourceId         : referenceSet.srcItemTypeId,
                              sourceCardinality: referenceSet.cardinality === '1:1' || referenceSet.cardinality === '1:n'
                                  ? '1'
                                  : 'N',
                              target           : referenceSet.destItemTypeId,
                              targetId         : referenceSet.destItemTypeId,
                              targetCardinality: referenceSet.cardinality === '1:1' || referenceSet.cardinality === 'n:1'
                                  ? '1'
                                  : referenceSet.cardinality === '1:n'
                                      ? 'N'
                                      : 'M',
                              num              : linkCounts.current[ referenceSetCountKey ],
                              reverse          : false
                          } );
                      }
                  }
                  else if ( type === 'data' ) {
                      for ( const reference of references ) {
                          const referenceSetCountKey = Math.min( reference.srcItemId, reference.destItemId ) + '_' + Math.max( reference.srcItemId, reference.destItemId );

                          if ( !linkCounts.current[ referenceSetCountKey ] ) {
                              linkCounts.current[ referenceSetCountKey ] = 0;
                          }

                          linkCounts.current[ referenceSetCountKey ]++;

                          retVal.push( {
                              id               : reference.id,
                              svgId            : `link_${reference.id}`,
                              label            : reference.referenceSet.referenceTypeLabel,
                              fullLabel        : reference.referenceSet.label,
                              cardinality      : reference.referenceSet.cardinality,
                              source           : reference.srcItemId,
                              sourceId         : reference.srcItemId,
                              sourceCardinality: reference.referenceSet.cardinality === '1:1' || reference.referenceSet.cardinality === '1:n'
                                  ? '1'
                                  : 'N',
                              target           : reference.destItemId,
                              targetId         : reference.destItemId,
                              targetCardinality: reference.referenceSet.cardinality === '1:1' || reference.referenceSet.cardinality === 'n:1'
                                  ? '1'
                                  : reference.referenceSet.cardinality === '1:n'
                                      ? 'N'
                                      : 'M',
                              num              : linkCounts.current[ referenceSetCountKey ]
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  dataStructures,
                  language,
                  messageCatalogTimestamp,
                  references,
                  resetViewToggle,
                  type
              ]
          ),
          nodes                                                     = useMemo(
              () => {
                  let retVal : Node[] | Node[][] = [];

                  cFlatNodes.current = [];

                  if ( type === 'meta' && dataStructures ) {
                      const itemTypes        = dataStructures.reduce(
                                ( acc, dataStructure ) => {
                                    const innerItemTypes = extractDataObjectsFromDataStructure( dataStructure ).reduce(
                                        ( innerRetVal, dataObject ) => {
                                            const itemType = metaModel[ ( dataObject.item.objectSchema ?? schema ) as Schema ].getItemType( dataObject.item.objectType ?? '' );

                                            if ( itemType ) {
                                                innerRetVal.push( itemType );
                                            }

                                            return innerRetVal;
                                        },
                                        [] as ItemType[]
                                    );

                                    for ( const itemType of innerItemTypes ) {
                                        if ( !acc.includes( itemType ) ) {
                                            acc.push( itemType );
                                        }
                                    }

                                    return acc;
                                },
                                [] as ItemType[]
                            ),
                            getReferenceSets = ( subDataStructure : DataStructure ) => {
                                const innerRetVal : ReferenceSet[] = [],
                                      srcItemType                  = subDataStructure.item.objectType;

                                if ( srcItemType ) {
                                    for ( const subSubDataStructure of subDataStructure.dataStructure ?? [] ) {
                                        const targetItemType = subSubDataStructure.item.objectType;

                                        if ( targetItemType && subSubDataStructure.reference?.referenceConfiguration ) {
                                            for ( const referenceTypeName of Object.keys( subSubDataStructure.reference.referenceConfiguration ) ) {
                                                const referenceSet = metaModel[ ( subSubDataStructure.item.objectSchema ?? schema ) as Schema ].getReferenceSetsForItemTypesAndReferenceType(
                                                    srcItemType,
                                                    targetItemType,
                                                    referenceTypeName,
                                                    subSubDataStructure.reference.referenceConfiguration[ referenceTypeName ].referenceDirection === 'SrcToDest'
                                                        ? 'src'
                                                        : 'dest'
                                                )?.[ 0 ];

                                                if ( referenceSet && !innerRetVal.includes( referenceSet ) ) {
                                                    innerRetVal.push( referenceSet );
                                                }
                                            }
                                        }

                                        const subReferenceSets = getReferenceSets( subSubDataStructure );

                                        for ( const referenceSet of subReferenceSets ) {
                                            if ( !innerRetVal.includes( referenceSet ) ) {
                                                innerRetVal.push( referenceSet );
                                            }
                                        }
                                    }
                                }

                                return innerRetVal;
                            },
                            referenceSets    = dataStructures.reduce(
                                ( acc, dataStructure ) => {
                                    const innerReferenceSets = getReferenceSets( dataStructure );

                                    for ( const referenceSet of innerReferenceSets ) {
                                        if ( !acc.includes( referenceSet ) ) {
                                            acc.push( referenceSet );
                                        }
                                    }

                                    return acc;
                                },
                                [] as ReferenceSet[]
                            );

                      for ( const itemType of itemTypes ) {
                          const svgId = `node_${itemType.id}`,
                                x     = svgNodesTransform?.[ svgId ]?.x
                                    ? svgNodesTransform[ svgId ].x
                                    : 0,
                                y     = svgNodesTransform?.[ svgId ]?.y
                                    ? svgNodesTransform[ svgId ].y
                                    : 0;

                          ( retVal as MetaNode[] ).push( {
                              id             : itemType.id,
                              svgId,
                              label          : itemType.label,
                              icon           : itemType.icon,
                              x,
                              y,
                              width          : 0,
                              height         : 0,
                              itemTypeId     : itemType.id,
                              fixPositions   : !Object.keys( svgNodesTransform ?? {} ).length,
                              linkCount      : referenceSets.filter( rS => rS.srcItemTypeId === itemType.id || rS.destItemTypeId === itemType.id ).length,
                              uniqueLinkCount: referenceSets.reduce(
                                  ( acc, rS ) => {
                                      if ( rS.srcItemTypeId === itemType.id && !acc.includes( rS.destItemTypeId ) ) {
                                          acc.push( rS.destItemTypeId );
                                      }
                                      else if ( rS.destItemTypeId === itemType.id && !acc.includes( rS.srcItemTypeId ) ) {
                                          acc.push( rS.srcItemTypeId );
                                      }

                                      return acc;
                                  },
                                  [] as number[]
                              ).length
                          } );
                      }

                      // apply layout
                      if ( retVal.length && !Object.keys( svgNodesTransform ?? {} ).length ) {
                          switch ( layout ) {
                              case 'auto':
                              case 'cise':
                                  ciseLayout( ( retVal as MetaNode[] ), links );
                                  break;

                              case 'euler':
                                  eulerLayout( ( retVal as MetaNode[] ), links );
                                  break;

                              default:
                                  ( retVal as MetaNode[] ).sort( ( a, b ) => {
                                      if ( a.uniqueLinkCount > b.uniqueLinkCount ) {
                                          return -1;
                                      }
                                      else if ( a.uniqueLinkCount < b.uniqueLinkCount ) {
                                          return 1;
                                      }
                                      else {
                                          return 0;
                                      }
                                  } );

                                  if ( retVal.length && !Object.keys( svgNodesTransform ?? {} ).length ) {
                                      const rNode = retVal[ 0 ] as MetaNode,
                                            rX    = graphWidth / 2 - 100,
                                            rY    = graphHeight / 2 - 50;

                                      rNode.x = graphWidth / 2;
                                      rNode.y = graphHeight / 2;

                                      for ( let i = 1; i < retVal.length; i++ ) {
                                          const node  = retVal[ i ] as MetaNode,
                                                alpha = ( 360 / ( retVal.length ) ) * i,
                                                x     = graphWidth / 2 + Math.cos( alpha / 180 * Math.PI ) * rX,
                                                y     = graphHeight / 2 + Math.sin( alpha / 180 * Math.PI ) * rY;

                                          node.x = x;
                                          node.y = y;
                                      }
                                  }

                                  break;
                          }
                      }
                  }
                  else if ( type === 'data' && dataConfig ) {
                      if ( graphType === 'tree' || graphType === 'tangledTree' || graphType === 'radialTree' ) {
                          const processedReferences : number[] = [],
                                usedSvgIds : string[]          = [],
                                sortNodesFnc                   = ( a : DataNode, b : DataNode ) => {
                                    if ( a.itemType > b.itemType ) {
                                        return 1;
                                    }
                                    else if ( a.itemType < b.itemType ) {
                                        return -1;
                                    }
                                    else if ( a.label > b.label ) {
                                        return 1;
                                    }
                                    else if ( a.label < b.label ) {
                                        return -1;
                                    }
                                    else {
                                        return 0;
                                    }
                                },
                                buildNode                      = ( item : Item, reference? : Reference, referenceDirection? : 'dest' | 'src' ) : DataNode => {
                                    let svgId = `node_${item.id}`,
                                        count = 1;

                                    while ( usedSvgIds.includes( svgId ) ) {
                                        svgId = `node_${item.id}_${count++}`;
                                    }

                                    usedSvgIds.push( svgId );

                                    const innerRetVal : DataNode = {
                                        id        : item.id,
                                        svgId,
                                        label     : item.displayAttributeValue,
                                        icon      : item.itemType.icon,
                                        itemTypeId: item.itemTypeId,
                                        itemType  : item.itemType.label,
                                        color     : itemTypeColorMapping.current[ item.itemTypeId ]
                                    };

                                    if ( reference && referenceDirection ) {
                                        innerRetVal.reference = [
                                            {
                                                referenceId  : reference?.id,
                                                referenceType: reference?.referenceSet.referenceTypeLabel,
                                                referenceDirection
                                            }
                                        ];
                                    }

                                    return innerRetVal;
                                },
                                getNodeChildren                = ( item : Item, onlyFirstReferences : boolean = true, onlyUniqueItems : boolean = false ) : DataNode[] | undefined => {
                                    const processedItems : number[] = [];

                                    let innerRetVal : DataNode[] | undefined = [];

                                    for ( const reference of references ) {
                                        if (
                                            ( !onlyFirstReferences || !processedReferences.includes( reference.id ) )
                                            && ( reference.srcItemId === item.id || reference.destItemId === item.id )
                                        ) {
                                            const nodeItem = reference.destItemId === item.id
                                                ? items.find( i => i.id === reference.srcItemId )
                                                : items.find( i => i.id === reference.destItemId );

                                            processedReferences.push( reference.id );

                                            if ( nodeItem ) {
                                                if ( !onlyUniqueItems || !processedItems.includes( nodeItem.id ) ) {
                                                    if ( !processedItems.includes( nodeItem.id ) ) {
                                                        processedItems.push( nodeItem.id );
                                                    }

                                                    innerRetVal.push( buildNode( nodeItem, reference, reference.destItemId === item.id ? 'src' : 'dest' ) );
                                                    cFlatNodes.current.push( innerRetVal[ innerRetVal.length - 1 ] );
                                                }
                                                else {
                                                    const node = cFlatNodes.current.find( n => n.id === nodeItem.id ) as DataNode;

                                                    if ( node && node.reference && !node.reference.find( r => r.referenceId === reference.id ) ) {
                                                        node.reference.push( {
                                                            referenceId       : reference.id,
                                                            referenceType     : reference.referenceSet.referenceTypeLabel,
                                                            referenceDirection: reference.destItemId === item.id ? 'src' : 'dest'
                                                        } );
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    if ( !innerRetVal.length ) {
                                        innerRetVal = undefined;
                                    }
                                    else {
                                        innerRetVal.sort( sortNodesFnc );
                                    }

                                    return innerRetVal;
                                };

                          for ( const item of rootItems ) {
                              ( retVal as DataNode[] ).push( buildNode( item ) );
                          }

                          cFlatNodes.current.push( ...( retVal as DataNode[] ) );

                          ( retVal as DataNode[] ).sort( sortNodesFnc );

                          if ( graphType === 'tree' || graphType === 'radialTree' ) {
                              let childNodes = retVal as DataNode[],
                                  depth      = dataDisplayConfig.referenceDepth;

                              while ( childNodes.length && depth > 0 ) {
                                  for ( const node of childNodes ) {
                                      const item     = items.find( i => i.id === node.id ),
                                            children = item ? getNodeChildren( item, true, true ) : undefined;

                                      if ( children ) {
                                          node.children = children;
                                      }
                                  }

                                  childNodes = ( [] as DataNode[] ).concat( ...childNodes.map( cN => cN.children ?? [] ) );
                                  depth--;
                              }
                          }
                          else {
                              const allChildren : DataNode[] = [];

                              let childNodes = retVal as DataNode[],
                                  depth      = dataDisplayConfig.referenceDepth;

                              retVal = [ retVal as DataNode[] ];

                              while ( childNodes.length && depth > 0 ) {
                                  const children : DataNode[] = [];

                                  for ( const node of childNodes ) {
                                      const item         = items.find( i => i.id === node.id ),
                                            nodeChildren = item ? getNodeChildren( item ) : undefined;

                                      if ( nodeChildren ) {
                                          for ( let i = 0; i < nodeChildren.length; i++ ) {
                                              if ( !allChildren.find( n => n.id === nodeChildren[ i ].id ) ) {
                                                  children.push( nodeChildren[ i ] );
                                              }
                                              if ( !allChildren.find( n => n.svgId === nodeChildren[ i ].svgId ) ) {
                                                  allChildren.push( nodeChildren[ i ] );
                                              }

                                              const childNode = children.find( n => n.id === nodeChildren[ i ].id );

                                              if ( childNode ) {
                                                  if ( !childNode.parents ) {
                                                      childNode.parents = [ node.id ];
                                                  }
                                                  else {
                                                      childNode.parents.push( node.id );
                                                  }
                                              }
                                          }
                                      }
                                  }

                                  children.sort( sortNodesFnc );

                                  retVal.push( children );
                                  childNodes = children;
                                  depth--;
                              }

                              cFlatNodes.current.push( ...allChildren );
                          }
                      }
                      else {
                          for ( const item of items ) {
                              const svgId = `node_${item.id}`,
                                    x     = svgNodesTransform?.[ svgId ]?.x
                                        ? svgNodesTransform[ svgId ].x
                                        : 0,
                                    y     = svgNodesTransform?.[ svgId ]?.y
                                        ? svgNodesTransform[ svgId ].y
                                        : 0;

                              ( retVal as MetaNode[] ).push( {
                                  id             : item.id,
                                  svgId,
                                  label          : item.displayAttributeValue,
                                  icon           : item.itemType.icon,
                                  color          : itemTypeColorMapping.current[ item.itemTypeId ],
                                  x,
                                  y,
                                  width          : 0,
                                  height         : 0,
                                  itemTypeId     : item.itemTypeId,
                                  isRoot         : rootItems.includes( item ),
                                  fixPositions   : !Object.keys( svgNodesTransform ?? {} ).length,
                                  linkCount      : references.filter( r => r.srcItemId === item.id || r.destItemId === item.id ).length,
                                  uniqueLinkCount: references.reduce(
                                      ( acc, r ) => {
                                          if ( r.srcItemId === item.id && !acc.includes( r.destItemId ) ) {
                                              acc.push( r.destItemId );
                                          }
                                          else if ( r.destItemId === item.id && !acc.includes( r.srcItemId ) ) {
                                              acc.push( r.srcItemId );
                                          }

                                          return acc;
                                      },
                                      [] as number[]
                                  ).length
                              } );
                          }

                          // apply layout
                          if ( retVal.length && !Object.keys( svgNodesTransform ?? {} ).length ) {
                              switch ( layout ) {
                                  case 'cise':
                                      ciseLayout( ( retVal as MetaNode[] ), links );
                                      break;

                                  case 'auto':
                                  case 'euler':
                                      eulerLayout( ( retVal as MetaNode[] ), links );
                                      break;

                                  default:
                                      ( retVal as MetaNode[] ).sort( ( a, b ) => {
                                          if ( a.uniqueLinkCount > b.uniqueLinkCount ) {
                                              return -1;
                                          }
                                          else if ( a.uniqueLinkCount < b.uniqueLinkCount ) {
                                              return 1;
                                          }
                                          else {
                                              return 0;
                                          }
                                      } );

                                      if ( retVal.length && !Object.keys( svgNodesTransform ?? {} ).length ) {
                                          const rNode = retVal[ 0 ] as MetaNode,
                                                rX    = graphWidth / 2 - 100,
                                                rY    = graphHeight / 2 - 50;

                                          rNode.x = graphWidth / 2;
                                          rNode.y = graphHeight / 2;

                                          for ( let i = 1; i < retVal.length; i++ ) {
                                              const node  = retVal[ i ] as MetaNode,
                                                    alpha = ( 360 / ( retVal.length ) ) * i,
                                                    x     = graphWidth / 2 + Math.cos( alpha / 180 * Math.PI ) * rX,
                                                    y     = graphHeight / 2 + Math.sin( alpha / 180 * Math.PI ) * rY;

                                              node.x = x;
                                              node.y = y;
                                          }
                                      }

                                      break;
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataStructures,
                  graphType,
                  items,
                  language,
                  layout,
                  messageCatalogTimestamp,
                  references,
                  resetViewToggle,
                  rootItems,
                  type
              ]
          ),
          drawLinks                                                 = useCallback(
              ( link : any ) => {
                  const sourceId   = link.sourceId,
                        targetId   = link.targetId,
                        sourceNode = ( cNodes.current as MetaNode[] ).find( n => n.id === sourceId ) as MetaNode,
                        targetNode = ( cNodes.current as MetaNode[] ).find( n => n.id === targetId ) as MetaNode;

                  let retVal : string = 'M 0 0 L 0 0';

                  if ( sourceNode && targetNode ) {
                      const referenceSetCountKey = Math.min( sourceId, targetId ) + '_' + Math.max( sourceId, targetId ),
                            sourceX              = sourceNode.x,
                            sourceY              = sourceNode.y,
                            sourceH              = sourceNode.height,
                            sourceW              = sourceNode.width,
                            targetX              = targetNode.x,
                            targetY              = targetNode.y,
                            targetH              = targetNode.height,
                            targetW              = targetNode.width;

                      let startX : number,
                          startY : number,
                          endX : number,
                          endY : number,
                          r : number | undefined,
                          controlX : number | undefined,
                          controlY : number | undefined;

                      // link to itself - draw an arc above node
                      if ( sourceId === targetId ) {
                          const offset = 10;

                          startX = targetX + targetW + 2;
                          startY = targetY + offset;
                          endX = sourceX + sourceW - offset;
                          endY = sourceY - 5;
                          r = 10 + link.num * 7;
                      }
                      // draw a line from source center to target center
                      else {
                          startX = sourceX + sourceW / 2;
                          startY = sourceY + sourceH / 2;
                          endX = targetX + targetW / 2;
                          endY = targetY + targetH / 2;

                          const a = endX - startX;

                          let b = endY - startY,
                              c = Math.hypot( a, b ),
                              alpha,
                              dx,
                              dy;

                          // correct start and end to the boundary box of the node
                          // start
                          alpha = b / c;
                          dx = Math.sqrt( Math.abs( Math.pow( sourceH / 2 / alpha, 2 ) - Math.pow( sourceH / 2, 2 ) ) );
                          alpha = a / c;
                          dy = Math.sqrt( Math.abs( Math.pow( sourceW / 2 / alpha, 2 ) - Math.pow( sourceW / 2, 2 ) ) );

                          if ( Math.abs( dx ) <= sourceW / 2 ) {
                              startX += ( targetX > sourceX ) ? dx : -dx;
                              startY += ( targetY > sourceY ) ? sourceH / 2 + 1 : -sourceH / 2 - 1;
                          }
                          else {
                              startY += ( targetY > sourceY ) ? dy : -dy;
                              startX += ( targetX > sourceX ) ? sourceW / 2 + 1 : -sourceW / 2 - 1;
                          }

                          // end
                          alpha = b / c;
                          dx = Math.sqrt( Math.abs( Math.pow( targetH / 2 / alpha, 2 ) - Math.pow( targetH / 2, 2 ) ) );
                          alpha = a / c;
                          dy = Math.sqrt( Math.abs( Math.pow( targetW / 2 / alpha, 2 ) - Math.pow( targetW / 2, 2 ) ) );

                          if ( Math.abs( dx ) <= targetW / 2 ) {
                              endX += ( targetX < sourceX ) ? dx : -dx;
                              endY += ( targetY < sourceY ) ? targetH / 2 + 1.5 : -targetH / 2 - 1.5;
                          }
                          else {
                              endY += ( targetY < sourceY ) ? dy : -dy;
                              endX += ( targetX < sourceX ) ? targetW / 2 + 1.5 : -targetW / 2 - 1.5;
                          }

                          // multiple references between nodes - otherwise just draw a line
                          if ( linkCounts.current[ referenceSetCountKey ] > 1 ) {
                              const isCountOdd = !!( linkCounts.current[ referenceSetCountKey ] % 2 );

                              let num = Math.ceil( linkCounts.current[ referenceSetCountKey ] / -2 ) - 1 + link.num;

                              if ( !isCountOdd && num >= 0 ) {
                                  num++;
                              }

                              // not first of an odd number - otherwise just draw a line
                              if ( num !== 0 ) {
                                  const diffX = endX - startX,
                                        diffY = endY - startY;

                                  controlX = startX + diffX / 2;
                                  controlY = startY + diffY / 2;

                                  c = Math.hypot( diffX, diffY );
                                  b = ( ( !isCountOdd ? -0.5 : 0 ) + Math.abs( num ) ) * 33;

                                  alpha = ( diffY ) / c;
                                  dx = ( num < 0 ? -1 : 1 ) * alpha * b;
                                  alpha = ( diffX ) / c;
                                  dy = ( num < 0 ? -1 : 1 ) * alpha * b;

                                  controlX += ( endY < startY ? -1 : 1 ) * dx;
                                  controlY += ( endY < startY ? 1 : -1 ) * dy;
                              }
                          }
                      }

                      // draw the line but always from left to right
                      if ( startX !== undefined && startY !== undefined && endX !== undefined && endY !== undefined ) {
                          if ( startX > endX ) {
                              link.reverse = true;

                              if ( r !== undefined ) {
                                  retVal = `M ${endX} ${endY}
                                            A ${r} ${r} -180 1 1 ${startX} ${startY}`;
                              }
                              else if ( controlX !== undefined && controlY !== undefined ) {
                                  retVal = `M ${endX} ${endY} 
                                            Q ${controlX} ${controlY} ${startX} ${startY}`;
                              }
                              else {
                                  retVal = `M ${endX} ${endY} 
                                            L ${startX} ${startY}`;
                              }

                              graphSvg.current
                                  ?.select( `#${link.svgId}_path` )
                                  .attr( 'marker-end', 'none' )
                                  .attr( 'marker-start', `url(#${arrowMarkerId})` );
                              graphSvg.current
                                  ?.select( `#${link.svgId}_shadow_path` )
                                  .attr( 'marker-end', 'none' )
                                  .attr( 'marker-start', `url(#${arrowMarkerId}_shadow)` );

                              graphSvg.current
                                  ?.select( `#${link.svgId}_cardinality_src` )
                                  .selectAll( 'textPath' )
                                  .text( link.targetCardinality );
                              graphSvg.current
                                  ?.select( `#${link.svgId}_cardinality_dest` )
                                  .selectAll( 'textPath' )
                                  .text( link.sourceCardinality );
                          }
                          else {
                              link.reverse = false;

                              if ( r !== undefined ) {
                                  retVal = `M ${startX} ${startY}
                                            A ${r} ${r} -180 1 0 ${endX} ${endY}`;
                              }
                              else if ( controlX !== undefined && controlY !== undefined ) {
                                  retVal = `M ${startX} ${startY} 
                                            Q ${controlX} ${controlY} ${endX} ${endY}`;
                              }
                              else {
                                  retVal = `M ${startX} ${startY} 
                                            L ${endX} ${endY}`;
                              }

                              graphSvg.current
                                  ?.select( `#${link.svgId}_path` )
                                  .attr( 'marker-end', `url(#${arrowMarkerId})` )
                                  .attr( 'marker-start', 'none' );
                              graphSvg.current
                                  ?.select( `#${link.svgId}_shadow_path` )
                                  .attr( 'marker-end', `url(#${arrowMarkerId}_shadow)` )
                                  .attr( 'marker-start', 'none' );

                              graphSvg.current
                                  ?.select( `#${link.svgId}_cardinality_src` )
                                  .selectAll( 'textPath' )
                                  .text( link.sourceCardinality );
                              graphSvg.current
                                  ?.select( `#${link.svgId}_cardinality_dest` )
                                  .selectAll( 'textPath' )
                                  .text( link.targetCardinality );
                          }

                          // link linkLabelLength > linkLength hide label and label background
                          graphSvg.current
                              ?.select( `#${link.svgId}_label` )
                              .attr( 'display', 'inherit' );

                          const linkLength      = Math.hypot( endX - startX, endY - startY ) - 20,
                                linkLabelLength = ( document.getElementById( `${link.svgId}_label` ) as unknown as SVGTextElement | undefined )?.getComputedTextLength() ?? 0;

                          if ( linkLabelLength > linkLength ) {
                              graphSvg.current
                                  ?.select( `#${link.svgId}_label` )
                                  .attr( 'display', 'none' );
                          }
                      }
                  }

                  d3.select( `#${link.svgId}_bg` )
                      .select( 'path' )
                      .attr( 'd', retVal );

                  return retVal;
              },
              [
                  'onlyOnce'
              ]
          ),
          positionNodeLabels                                        = useCallback(
              () => {
                  graphSvg.current?.selectAll( '.yn-node-label' )
                      .attr( 'x', d => {
                          const selection = d3.select( `#${( d as Node ).svgId}` );

                          let retVal = '';

                          if ( selection.size() ) {
                              retVal = selection.attr( 'x' );
                          }

                          return retVal;
                      } )
                      .attr( 'y', d => {
                          const selection = d3.select( `#${( d as Node ).svgId}` );

                          let retVal = '';

                          if ( selection.size() ) {
                              retVal = selection.attr( 'y' );
                          }

                          return retVal;
                      } )
                      .attr( 'transform', d => {
                          const selection = d3.select( `#${( d as Node ).svgId}` );

                          let retVal = '';

                          if ( selection.size() ) {
                              retVal = selection.attr( 'transform' );
                          }

                          return retVal;
                      } );
              },
              [
                  'onlyOnce'
              ]
          ),
          selectNodes                                               = useCallback(
              ( nodesToSelect : number[] ) => {
                  graphSvg.current
                      ?.select( `#${nodesId}` )
                      .selectAll( 'rect' )
                      .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Node ).svgId + '_background' )
                      .attr( 'class', d => {
                          const retVal : string[] = [];

                          if ( ( d as MetaNode ).isRoot ) {
                              retVal.push( 'yn-graph-root-node' );
                          }
                          else {
                              retVal.push( 'yn-graph-node' );
                          }

                          if ( nodesToSelect?.includes( ( d as MetaNode ).id ) ) {
                              retVal.push( 'yn-graph-active-node' );
                          }

                          return retVal.join( ' ' );
                      } );

                  if ( nodesToSelect?.length ) {
                      const activeNodes : number[] = ( [] as number[] ).concat( nodesToSelect ),
                            activeLinks : number[] = [];

                      for ( const nodeToSelect of nodesToSelect ) {
                          const nodeLinks   = cLinks.current.filter( l => l.sourceId === nodeToSelect || l.targetId === nodeToSelect ),
                                linkedLinks = nodeLinks.map( l => l.id ),
                                linkedNodes = nodeLinks.map( l => l.sourceId !== nodeToSelect ? l.sourceId : l.targetId );

                          for ( const linkId of linkedLinks ) {
                              if ( !activeLinks.includes( linkId ) ) {
                                  activeLinks.push( linkId );
                              }
                          }

                          for ( const nodeId of linkedNodes ) {
                              if ( !activeNodes.includes( nodeId ) ) {
                                  activeNodes.push( nodeId );
                              }
                          }
                      }

                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', d => activeLinks.includes( ( d as Link ).id ) ? 1 : .2 );

                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', d => activeNodes.includes( ( d as Node ).id ) ? 1 : .2 );
                  }
                  else {
                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', 1 );

                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', 1 );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          getDataNodeTooltip                                        = useCallback(
              ( node : Node | PositionedDataNode, nodeType : 'data' | 'positionedData' = 'data', additionalContent? : string ) => {
                  const nodeItem = global.data[ dataContext ]?.items[ node.id ];

                  let retVal : React.ReactNode = null;

                  if ( nodeItem ) {
                      const parts : string[] = [
                          `<span class="yn-secondary">${nodeItem.itemType.label}</span><br/>`,
                          `<strong>${nodeItem.displayAttributeValue}</strong><br/>`,
                          `<br/>`,
                          `${__( 'YN.base.finder.item.idLabel' )}: ${nodeItem.id}<br/>`,
                          `${getItemKeyDisplayName( nodeItem.itemType.id, nodeItem.schema )}: ${nodeItem.key}<br/>`,
                          `${getItemNameDisplayName( nodeItem.itemType.id, nodeItem.schema )}: ${nodeItem.name}<br/>`
                      ];

                      if ( nodeType === 'positionedData' ) {
                          const dNode = node as PositionedDataNode;

                          if ( dNode.bundle?.links?.length ) {
                              parts.push( `<br/>${__( 'YN.base.frontend.graph.parents.label' )}:<br/>` );

                              for ( const link of dNode.bundle.links ) {
                                  if ( link.target === dNode.id ) {
                                      parts.push( `${link.targetLabel}` );
                                  }
                              }
                          }

                          if ( dNode.bundles?.length ) {
                              parts.push( `<br/>${__( 'YN.base.frontend.graph.children.label' )}:<br/>` );

                              for ( const bundle of dNode.bundles ) {
                                  for ( const link of bundle.links ?? [] ) {
                                      if ( link.source === dNode.id ) {
                                          parts.push( `${link.sourceLabel}` );
                                      }
                                  }
                              }
                          }
                      }

                      if ( additionalContent ) {
                          parts.push( `<br/>${additionalContent}` );
                      }

                      retVal = parse( parts.join( '' ) );
                  }

                  return retVal;
              },
              [
                  dataContext
              ]
          ),
          openPanelForNodeItem                                      = useCallback(
              ( item : Item, event : any ) => {
                  dispatch( openPanelForItemType(
                      item.schema,
                      item.itemType.name,
                      {
                          schema    : item.schema,
                          rootItemId: item.id
                      },
                      undefined,
                      event,
                      undefined,
                      [ 'FinderOverview', 'Overview', 'XmlImport' ]
                  ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          fixMinZoomLevel                                           = useCallback(
              () => {
                  if ( cZoom.current ) {
                      const svgBox               = document.getElementById( `${elementId}_svg` )?.getBoundingClientRect(),
                            zoomTargetNode       = document.getElementById( `${zoomTargetElementId}` ),
                            currentTransformAttr = zoomTargetNode?.getAttribute( 'transform' ),
                            currentTransform     = new d3.ZoomTransform(
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*scale\(([0-9.]+)/, '$1' ) ) ) / 10000
                                    : 1,
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(([0-9.\-]+),.*/, '$1' ) ) ) / 10000
                                    : 0,
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(.*,([0-9.\-]+)/, '$1' ) ) ) / 10000
                                    : 0
                            ),
                            currentBox           = zoomTargetNode?.getBoundingClientRect();

                      if ( svgBox && currentBox?.width ) {
                          const minZoomLevel = Math.round( 10000 * Math.min( .9 * currentTransform.k * svgBox.width / currentBox.width, .9 * currentTransform.k * svgBox.height / currentBox.height ) * .6 ) / 10000;

                          cZoom.current.scaleExtent( [ minZoomLevel, 10 ] );
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          fitSvgGraphToCenter                                       = useCallback(
              () => {
                  const svgBox               = document.getElementById( `${elementId}_svg` )?.getBoundingClientRect(),
                        zoomTargetNode       = document.getElementById( `${zoomTargetElementId}` ),
                        currentTransformAttr = zoomTargetNode?.getAttribute( 'transform' ),
                        currentTransform     = new d3.ZoomTransform(
                            currentTransformAttr
                                ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*scale\(([0-9.]+)/, '$1' ) ) ) / 10000
                                : 1,
                            currentTransformAttr
                                ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(([0-9.\-]+),.*/, '$1' ) ) ) / 10000
                                : 0,
                            currentTransformAttr
                                ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(.*,([0-9.\-]+)/, '$1' ) ) ) / 10000
                                : 0
                        ),
                        currentBox           = zoomTargetNode?.getBoundingClientRect();

                  if ( svgBox?.width && currentBox?.width ) {
                      const k                                        = Math.min( 1, Math.round( 10000 * Math.min( .9 * currentTransform.k * svgBox.width / currentBox.width, .9 * currentTransform.k * svgBox.height / currentBox.height ) ) / 10000 ),
                            fK                                       = k / currentTransform.k,
                            offsetX                                  = svgBox.x - fK * ( currentBox.x - currentTransform.x ),
                            offsetY                                  = svgBox.y - fK * ( currentBox.y - currentTransform.y ),
                            x                                        = Math.round( 10000 * ( offsetX - .5 * fK * currentBox.width + .5 * svgBox.width ) ) / 10000,
                            y                                        = Math.round( 10000 * ( offsetY - .5 * fK * currentBox.height + .5 * svgBox.height ) ) / 10000,
                            transform                                = new d3.ZoomTransform(
                                k,
                                x,
                                y
                            ),
                            newSvgGraphTransform : SvgGraphTransform = {
                                x,
                                y,
                                k
                            };

                      graphSvg.current
                          ?.select( `#${zoomTargetElementId}` )
                          .attr( 'transform', transform.toString() );

                      if ( setSvgGraphTransformTimeoutId.current ) {
                          clearTimeout( setSvgGraphTransformTimeoutId.current );
                      }
                      setSvgGraphTransformTimeoutId.current = setTimeout(
                          () => {
                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'svgGraphTransform', newSvgGraphTransform );
                              }

                              dispatch( setParam( uid, 'svgGraphTransform', newSvgGraphTransform ) );
                          },
                          100
                      );

                      fixMinZoomLevel();

                      onZoom( { transform: newSvgGraphTransform } );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onFullscreenChange                                        = useCallback(
              () => {
                  if ( document.fullscreenElement ) {
                      setFullScreen( true );
                  }
                  else {
                      setFullScreen( false );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onSimulationTick                                          = useCallback(
              () => {
                  const newSvgNodesTransform : SvgNodesTransform = {};

                  graphSvg.current
                      ?.select( `#${nodesId}` )
                      .selectAll( 'g' )
                      .attr( 'transform', d => {
                          newSvgNodesTransform[ ( d as MetaNode ).svgId ] = {
                              x: ( d as MetaNode ).x,
                              y: ( d as MetaNode ).y,
                              k: 1
                          };

                          return `translate( ${( d as MetaNode ).x}, ${( d as MetaNode ).y} )`;
                      } )
                      .attr( 'x', d => ( d as MetaNode ).x )
                      .attr( 'y', d => ( d as MetaNode ).y );

                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'g' )
                      .selectAll( 'path' )
                      .attr( 'd', drawLinks );

                  positionNodeLabels();

                  if ( !userDrag.current ) {
                      if ( !userZoom.current ) {
                          fitSvgGraphToCenter();
                      }
                      else {
                          fixMinZoomLevel();
                      }
                  }

                  if ( setSvgNodesTransformTimeoutId.current ) {
                      clearTimeout( setSvgNodesTransformTimeoutId.current );
                  }
                  setSvgNodesTransformTimeoutId.current = setTimeout(
                      () => {
                          if ( setFormPersistentSubPanelProps ) {
                              setFormPersistentSubPanelProps( 'svgNodesTransform', newSvgNodesTransform );
                          }

                          dispatch( setParam( uid, 'svgNodesTransform', newSvgNodesTransform ) );
                      },
                      100
                  );
              },
              [
                  setFormPersistentSubPanelProps,
                  uid
              ]
          ),
          onMetaLinkClick                                           = useCallback(
              ( event : any, link : MetaLink ) => {
                  //console.log( 'onLinkClick' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onMetaLinkOver                                            = useCallback(
              ( event : any, link : MetaLink ) => {
                  setTooltipContent( `${link.fullLabel} (${link.cardinality})` );
                  setTooltipTarget( event.target );

                  if (
                      !cSelectedNodes.current.length
                      || cSelectedNodes.current.includes( link.sourceId )
                      || cSelectedNodes.current.includes( link.targetId )
                  ) {
                      // fade off all but not the hovered links
                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'g' )
                          .filter( d => ( d as Link ).id !== link.id )
                          .attr( 'opacity', .2 );

                      // mark link hovered
                      graphSvg.current
                          ?.select( `#${link.svgId}` )
                          .selectAll( 'path' )
                          .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === link.svgId + '_path' )
                          .attr( `marker-${( link as MetaLink ).reverse ? 'start' : 'end'}`, `url(#${arrowMarkerId}_hover)` )
                          .attr( 'class', ( d, i, nodeList ) => {
                              const el = nodeList[ i ] as SVGElement | undefined;

                              let retVal : string | null = null;

                              if ( el ) {
                                  el.classList.add( 'yn-graph-link-hover' );
                                  retVal = el.classList.toString();
                              }

                              return retVal;
                          } );

                      // fade off all but not hovered link node backgrounds
                      graphSvg.current
                          ?.select( `#${nodesId}_bg` )
                          .selectAll( 'g' )
                          .filter( d => ( d as Node ).id !== link.sourceId && ( d as Node ).id !== link.targetId )
                          .attr( 'opacity', 0 );

                      // raise link node labels
                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .filter( ( d, i, nodeList ) => ( ( nodeList[ i ] as SVGElement | undefined )?.id === `node_${link.sourceId}_label` || ( nodeList[ i ] as SVGElement | undefined )?.id === `node_${link.targetId}_label` ) )
                          .raise();

                      // fade off all but not hovered link nodes
                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .filter( d => ( d as Node ).id !== link.sourceId && ( d as Node ).id !== link.targetId )
                          .attr( 'opacity', .2 );

                      if ( link.sourceId !== link.targetId ) {
                          const srcNode  = ( cNodes.current as MetaNode[] ).find( n => n.id === link.sourceId ),
                                destNode = ( cNodes.current as MetaNode[] ).find( n => n.id === link.targetId );

                          if ( srcNode ) {
                              graphSvg.current
                                  ?.select( `#${srcNode.svgId}_background` )
                                  .attr( 'class', ( d, i, nodeList ) => {
                                      const el = nodeList[ i ] as SVGElement | undefined;

                                      let retVal : string | null = null;

                                      if ( el ) {
                                          el.classList.add( 'yn-graph-src-node' );
                                          retVal = el.classList.toString();
                                      }

                                      return retVal;
                                  } );
                          }

                          if ( destNode ) {
                              graphSvg.current
                                  ?.select( `#${destNode.svgId}_background` )
                                  .attr( 'class', ( d, i, nodeList ) => {
                                      const el = nodeList[ i ] as SVGElement | undefined;

                                      let retVal : string | null = null;

                                      if ( el ) {
                                          el.classList.add( 'yn-graph-dest-node' );
                                          retVal = el.classList.toString();
                                      }

                                      return retVal;
                                  } );
                          }
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onMetaLinkOut                                             = useCallback(
              ( event : any, link : MetaLink ) => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  if (
                      !cSelectedNodes.current.length
                      || cSelectedNodes.current.includes( link.sourceId )
                      || cSelectedNodes.current.includes( link.targetId )
                  ) {
                      // fade on all links
                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', 1 );

                      // unmark link hovered
                      graphSvg.current
                          ?.select( `#${link.svgId}` )
                          .selectAll( 'path' )
                          .attr( `marker-${( link as MetaLink ).reverse ? 'start' : 'end'}`, `url(#${arrowMarkerId})` )
                          .attr( 'class', ( d, i, nodeList ) => {
                              const el = nodeList[ i ] as SVGElement | undefined;

                              let retVal : string | null = null;

                              if ( el ) {
                                  el.classList.remove( 'yn-graph-link-hover' );
                                  retVal = el.classList.toString();
                              }

                              return retVal;
                          } );

                      // fade on all node backgrounds
                      graphSvg.current
                          ?.select( `#${nodesId}_bg` )
                          .selectAll( 'g' )
                          .attr( 'opacity', 1 );

                      // lower link node labels
                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Link ).svgId + '_label' )
                          .lower();

                      // fade on all but not hovered link nodes
                      graphSvg.current
                          ?.select( `#${nodesId}` )
                          .selectAll( 'g' )
                          .attr( 'opacity', 1 );

                      if ( link.sourceId !== link.targetId ) {
                          const srcNode  = ( cNodes.current as MetaNode[] ).find( n => n.id === link.sourceId ),
                                destNode = ( cNodes.current as MetaNode[] ).find( n => n.id === link.targetId );

                          if ( srcNode ) {
                              graphSvg.current
                                  ?.select( `#${srcNode.svgId}_background` )
                                  .attr( 'class', ( d, i, nodeList ) => {
                                      const el = nodeList[ i ] as SVGElement | undefined;

                                      let retVal : string | null = null;

                                      if ( el ) {
                                          el.classList.remove( 'yn-graph-src-node' );
                                          retVal = el.classList.toString();
                                      }

                                      return retVal;
                                  } );
                          }

                          if ( destNode ) {
                              graphSvg.current
                                  ?.select( `#${destNode.svgId}_background` )
                                  .attr( 'class', ( d, i, nodeList ) => {
                                      const el = nodeList[ i ] as SVGElement | undefined;

                                      let retVal : string | null = null;

                                      if ( el ) {
                                          el.classList.remove( 'yn-graph-dest-node' );
                                          retVal = el.classList.toString();
                                      }

                                      return retVal;
                                  } );
                          }
                      }

                      selectNodes( cSelectedNodes.current );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onMetaNodeClick                                           = useCallback(
              ( event : any, node : MetaNode ) => {
                  if ( !justDblClicked.current ) {
                      if ( nodeClickTimeoutId.current ) {
                          clearTimeout( nodeClickTimeoutId.current );
                      }

                      nodeClickTimeoutId.current = setTimeout(
                          () => {
                              const newSelectedNodes = ( [] as number[] ).concat( cSelectedNodes.current ?? [] );

                              if ( newSelectedNodes.includes( node.id ) ) {
                                  newSelectedNodes.splice( newSelectedNodes.findIndex( nSN => nSN === node.id ), 1 );
                              }
                              else {
                                  newSelectedNodes.push( node.id );
                              }

                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'selectedNodes', newSelectedNodes );
                              }

                              dispatch( setParam( uid, 'selectedNodes', newSelectedNodes ) );
                          },
                          15
                      );
                  }

                  justDblClicked.current = false;

                  svgSimulation.current?.stop();
                  if ( setSvgNodesTransformTimeoutId.current ) {
                      clearTimeout( setSvgNodesTransformTimeoutId.current );
                  }

                  event.stopPropagation();
              },
              [
                  setFormPersistentSubPanelProps,
                  uid
              ]
          ),
          onMetaNodeDblClick                                        = useCallback(
              ( event : any, node : MetaNode ) => {
                  if ( type === 'data' ) {
                      const nodeItem = global.data[ dataContext ]?.items[ node.id ];

                      if ( nodeClickTimeoutId.current ) {
                          clearTimeout( nodeClickTimeoutId.current );
                      }

                      if ( nodeItem ) {
                          openPanelForNodeItem( nodeItem, event );
                      }
                  }

                  justDblClicked.current = true;

                  svgSimulation.current?.stop();
                  if ( setSvgNodesTransformTimeoutId.current ) {
                      clearTimeout( setSvgNodesTransformTimeoutId.current );
                  }

                  event.stopPropagation();
              },
              [
                  dataContext,
                  openPanelForNodeItem,
                  type
              ]
          ),
          onMetaNodeOver                                            = useCallback(
              ( event : any, node : MetaNode ) => {
                  const nodeLinks              = cLinks.current.filter( l => l.sourceId === node.id || l.targetId === node.id ),
                        activeLinks            = nodeLinks.map( l => l.id ),
                        activeNodes : number[] = ( [ node.id ] as number[] ).concat( nodeLinks.map( l => l.sourceId !== node.id ? l.sourceId : l.targetId ) );

                  if ( type === 'meta' ) {
                      setTooltipContent( parse( `<strong>${node.label}</strong><br/><br/>${nodeLinks.map( l => `${l.fullLabel} (${( l as MetaLink ).cardinality})` ).join( '<br/>' )}` ) );
                  }
                  else {
                      setTooltipContent( getDataNodeTooltip( node ) );
                  }
                  setTooltipTarget( event.target );

                  // mark the node as hovered
                  graphSvg.current
                      ?.select( `#${node.svgId}_background` )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-node-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  if ( type !== 'meta' && iconNodeOnly ) {
                      // raise node label
                      graphSvg.current
                          ?.select( `#${node.svgId}_label` )
                          .raise()
                          .select( 'rect' )
                          .attr( 'opacity', '1' );

                      // fade of all but not hovered node labels
                      graphSvg.current
                          ?.selectAll( '.yn-node-label-text' )
                          .filter( d => ( d as MetaNode ).id !== node.id )
                          .attr( 'opacity', '.2' );
                  }

                  // mark src links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'path' )
                      .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Link ).svgId + '_path' && ( d as Link ).sourceId === node.id && ( d as Link ).targetId !== node.id )
                      .attr( 'marker-end', d => ( d as MetaLink ).reverse
                          ? 'none'
                          : `url(#${arrowMarkerId}_src)` )
                      .attr( 'marker-start', d => ( d as MetaLink ).reverse
                          ? `url(#${arrowMarkerId}_src)`
                          : 'none' )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-src-link' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  // mark dest links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'path' )
                      .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Link ).svgId + '_path' && ( d as Link ).targetId === node.id && ( d as Link ).sourceId !== node.id )
                      .attr( 'marker-end', d => ( d as MetaLink ).reverse
                          ? 'none'
                          : `url(#${arrowMarkerId}_dest)` )
                      .attr( 'marker-start', d => ( d as MetaLink ).reverse
                          ? `url(#${arrowMarkerId}_dest)`
                          : 'none' )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-dest-link' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  // mark circle links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'path' )
                      .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Link ).svgId + '_path' && ( d as Link ).sourceId === node.id && ( d as Link ).targetId === node.id )
                      .attr( 'marker-end', d => ( d as MetaLink ).reverse
                          ? 'none'
                          : `url(#${arrowMarkerId}_circle)` )
                      .attr( 'marker-start', d => ( d as MetaLink ).reverse
                          ? `url(#${arrowMarkerId}_circle)`
                          : 'none' )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-circle-link' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  // fade out other links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'g' )
                      .attr( 'opacity', d => activeLinks.includes( ( d as Link ).id ) ? 1 : .2 );

                  // fade out all but not referenced nodes
                  graphSvg.current
                      ?.select( `#${nodesId}` )
                      .selectAll( 'g' )
                      .attr( 'opacity', d => activeNodes.includes( ( d as Node ).id ) ? 1 : .2 );
              },
              [
                  getDataNodeTooltip,
                  graphType,
                  iconNodeOnly,
                  linksId,
                  type
              ]
          ),
          onMetaNodeOut                                             = useCallback(
              ( event : any, node : MetaNode ) => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  // unmark the node as hovered
                  graphSvg.current?.select( `#${node.svgId}_background` )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.remove( 'yn-graph-node-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  if ( type !== 'meta' && iconNodeOnly ) {
                      // lower node label
                      graphSvg.current
                          ?.select( `#${node.svgId}_label` )
                          .lower()
                          .select( 'rect' )
                          .attr( 'opacity', '0' );

                      // fade on all node labels
                      graphSvg.current
                          ?.selectAll( '.yn-node-label-text' )
                          .filter( d => ( d as MetaNode ).id !== node.id )
                          .attr( 'opacity', '1' );
                  }

                  // unmark all links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'path' )
                      .filter( ( d, i, nodeList ) => ( nodeList[ i ] as SVGElement | undefined )?.id === ( d as Link ).svgId + '_path' )
                      .attr( 'marker-end', d => ( d as MetaLink ).reverse ? 'none' : `url(#${arrowMarkerId})` )
                      .attr( 'marker-start', d => ( d as MetaLink ).reverse ? `url(#${arrowMarkerId})` : 'none' )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.remove( 'yn-graph-src-link' );
                              el.classList.remove( 'yn-graph-dest-link' );
                              el.classList.remove( 'yn-graph-circle-link' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );

                  // fade out other links
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'g' )
                      .attr( 'opacity', 1 );

                  // fade out all but not referenced nodes
                  graphSvg.current
                      ?.select( `#${nodesId}` )
                      .selectAll( 'g' )
                      .attr( 'opacity', 1 );

                  selectNodes( cSelectedNodes.current );
              },
              [
                  iconNodeOnly,
                  linksId,
                  type
              ]
          ),
          onMetaNodeDragStart                                       = useCallback(
              ( event : any, node : MetaNode ) => {
                  if ( nodeClickTimeoutId.current ) {
                      clearTimeout( nodeClickTimeoutId.current );
                  }

                  nodeClickTimeoutId.current = setTimeout(
                      () => {
                          event.subject.fx = event.subject.x;
                          event.subject.fy = event.subject.y;

                          userDrag.current = true;

                          if ( layout !== 'cise' && !event.active ) {
                              svgSimulation.current?.alphaTarget( 0.05 ).restart();
                          }
                      },
                      15
                  );

              },
              [
                  'onlyOnce'
              ]
          ),
          onMetaNodeDrag                                            = useCallback(
              ( event : any, node : MetaNode ) => {
                  event.subject.fx = event.x;
                  event.subject.fy = event.y;
              },
              [
                  'onlyOnce'
              ]
          ),
          onMetaNodeDragEnd                                         = useCallback(
              ( event : any, node : MetaNode ) => {
                  event.subject.fx = null;
                  event.subject.fy = null;

                  if ( !event.active ) {
                      svgSimulation.current?.alphaTarget( 0 ).stop();
                  }

                  userDrag.current = false;
              },
              [
                  'onlyOnce'
              ]
          ),
          onPositionedDataLinkClick                                 = useCallback(
              ( event : any, link : PositionedDataLink ) => {
                  //console.log( 'onLinkClick' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onPositionedDataLinkOver                                  = useCallback(
              ( event : any, link : PositionedDataLink ) => {
                  const svgEl       = document.getElementById( `${elementId}_svg` ) as unknown as SVGSVGElement | null,
                        transformEl = document.getElementById( `${zoomTargetElementId}` ) as unknown as SVGGElement | null;

                  let tooltipLinks = link.bundle.links,
                      eventX       = 0;

                  if ( svgEl && transformEl ) {
                      const pt = svgEl.createSVGPoint();

                      pt.x = event.clientX;
                      pt.y = event.clientY;

                      eventX = pt.matrixTransform( transformEl.getCTM()?.inverse()?.multiply( svgEl.getScreenCTM()?.inverse() ) ).x;
                  }

                  if ( eventX && eventX < link.bundle.x - 3 ) {
                      tooltipLinks = link.bundle.links?.filter( l => l.source === link.source );
                  }
                  else if ( eventX && eventX > link.bundle.x + 3 ) {
                      tooltipLinks = link.bundle.links?.filter( l => l.target === link.target );
                  }

                  if ( tooltipLinks?.length ) {
                      const tooltipLinksSvgIds = tooltipLinks.map( l => l.svgId );

                      setTooltipContent( parse( tooltipLinks.map( l => l.fullLabel ).join( '<br/>' ) ) );
                      setTooltipTarget( event.target );

                      // raise hovered link
                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'path.link' )
                          .filter( ( d, i, nodeList ) => {
                              const linkEl = nodeList[ i ] as SVGElement | undefined;

                              return tooltipLinksSvgIds.includes( linkEl?.id ?? '' );
                          } )
                          .raise();

                      // mark all other links as inactive
                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'path.link' )
                          .filter( ( d, i, nodeList ) => {
                              const linkEl = nodeList[ i ] as SVGElement | undefined;

                              return !tooltipLinksSvgIds.includes( linkEl?.id ?? '' );
                          } )
                          .attr( 'class', ( d, i, nodeList ) => {
                              const el = nodeList[ i ] as SVGElement | undefined;

                              let retVal : string | null = null;

                              if ( el ) {
                                  el.classList.add( 'yn-graph-link-inactive' );
                                  retVal = el.classList.toString();
                              }

                              return retVal;
                          } );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onPositionedDataLinkOut                                   = useCallback(
              ( event : any, link : PositionedDataLink ) => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  // unmark all links as inactive
                  graphSvg.current
                      ?.select( `#${linksId}` )
                      .selectAll( 'path.link' )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.remove( 'yn-graph-link-inactive' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataLinkClick                                           = useCallback(
              ( event : any, link : d3.HierarchyPointLink<DataNode> | d3.HierarchyLink<DataNode> ) => {
                  //console.log( 'onLinkClick' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataLinkOver                                            = useCallback(
              ( event : any, link : d3.HierarchyPointLink<DataNode> | d3.HierarchyLink<DataNode> ) => {
                  const linkPath = `#${link.target.data.svgId}_path`,
                        tooltip  = link.target.data.reference!
                            .map( r => global.data[ dataContext ].references[ r.referenceId ].referenceSet.label )
                            .join( '<br/>' );

                  setTooltipContent( parse( tooltip ) );
                  setTooltipTarget( event.target );

                  graphSvg.current
                      ?.select( linkPath )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-link-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataLinkOut                                             = useCallback(
              ( event : any, link : d3.HierarchyPointLink<DataNode> | d3.HierarchyLink<DataNode> ) => {
                  const newTooltipTarget = `#${link.target.data.svgId}_path`;

                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  graphSvg.current
                      ?.select( newTooltipTarget )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.remove( 'yn-graph-link-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataNodeClick                                           = useCallback(
              ( event : any, node : DataNode ) => {
                  if ( !justDblClicked.current ) {
                      if ( nodeClickTimeoutId.current ) {
                          clearTimeout( nodeClickTimeoutId.current );
                      }

                      /*nodeClickTimeoutId.current = setTimeout(
                          () => {
                          },
                          15
                      );*/
                  }

                  justDblClicked.current = false;
                  event.stopPropagation();
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataDoubleClick                                         = useCallback(
              ( event : any, node : DataNode ) => {
                  if ( nodeClickTimeoutId.current ) {
                      clearTimeout( nodeClickTimeoutId.current );
                  }


                  const nodeItem = global.data[ dataContext ]?.items[ node.id ];

                  if ( nodeItem ) {
                      openPanelForNodeItem( nodeItem, event );
                  }

                  justDblClicked.current = true;
                  event.stopPropagation();
              },
              [
                  dataContext,
                  openPanelForNodeItem
              ]
          ),
          onPositionedDataNodeOver                                  = useCallback(
              ( event : any, node : PositionedDataNode ) => {
                  setTooltipContent( getDataNodeTooltip( node, 'positionedData' ) );
                  setTooltipTarget( event.target );

                  if ( !cSelectedNodes.current.length || cSelectedNodes.current.includes( node.id ) ) {
                      const sameNodes = cFlatNodes.current?.filter( n => n.id === node.id );

                      for ( const n of sameNodes ) {
                          graphSvg.current
                              ?.select( `#${n.svgId}_background` )
                              .attr( 'class', ( d, i, nodeList ) => {
                                  const el = nodeList[ i ] as SVGElement | undefined;

                                  let retVal : string | null = null;

                                  if ( el ) {
                                      el.classList.add( 'yn-graph-node-hover' );
                                      retVal = el.classList.toString();
                                  }

                                  return retVal;
                              } );
                      }

                      if ( ( node as PositionedDataNode ).bundle ) {
                          const bundle        = ( node as PositionedDataNode ).bundle!,
                                bundleSvgId   = `bundle_${bundle.id}`,
                                bundles       = ( node as PositionedDataNode ).bundles,
                                bundlesSvgIds = bundles?.map( b => `bundle_${b.id}` ) ?? [];

                          graphSvg.current
                              ?.select( `#${linksId}` )
                              .selectAll( 'g' )
                              .filter( ( d, i, nodeList ) => {
                                  const linkEl = nodeList[ i ] as SVGElement | undefined;

                                  return !!linkEl?.classList.contains( 'bundle' ) && linkEl?.id !== bundleSvgId && !bundlesSvgIds.includes( linkEl?.id ?? '' );
                              } )
                              .selectAll( 'path.link' )
                              .attr( 'class', ( d, i, nodeList ) => {
                                  const el = nodeList[ i ] as SVGElement | undefined;

                                  let retVal : string | null = null;

                                  if ( el ) {
                                      el.classList.add( 'yn-graph-link-inactive' );
                                      retVal = el.classList.toString();
                                  }

                                  return retVal;
                              } );
                      }
                  }
              },
              [
                  getDataNodeTooltip
              ]
          ),
          onPositionedDataNodeOut                                   = useCallback(
              ( event : any, node : DataNode ) => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  if ( !cSelectedNodes.current.length || cSelectedNodes.current.includes( node.id ) ) {
                      const sameNodes = cFlatNodes.current?.filter( n => n.id === node.id );

                      for ( const n of sameNodes ) {
                          graphSvg.current?.select( `#${n.svgId}_background` )
                              .attr( 'class', ( d, i, nodeList ) => {
                                  const el = nodeList[ i ] as SVGElement | undefined;

                                  let retVal : string | null = null;

                                  if ( el ) {
                                      el.classList.remove( 'yn-graph-node-hover' );
                                      retVal = el.classList.toString();
                                  }

                                  return retVal;
                              } );
                      }

                      graphSvg.current
                          ?.select( `#${linksId}` )
                          .selectAll( 'g' )
                          .selectAll( 'path.link' )
                          .attr( 'class', ( d, i, nodeList ) => {
                              const el = nodeList[ i ] as SVGElement | undefined;

                              let retVal : string | null = null;

                              if ( el ) {
                                  el.classList.remove( 'yn-graph-link-inactive' );
                                  retVal = el.classList.toString();
                              }

                              return retVal;
                          } );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onDataNodeOver                                            = useCallback(
              ( event : any, node : d3.HierarchyNode<DataNode> ) => {
                  setTooltipContent( getDataNodeTooltip( node.data, 'data', node.data.reference?.map( r => global.data[ dataContext ].references[ r.referenceId ].referenceSet.label ).join( '<br/>' ) ) );
                  setTooltipTarget( event.target );

                  graphSvg.current
                      ?.select( `#${node.data.svgId}_background` )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.add( 'yn-graph-node-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );
              },
              [
                  getDataNodeTooltip
              ]
          ),
          onDataNodeOut                                             = useCallback(
              ( event : any, node : d3.HierarchyNode<DataNode> ) => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );

                  graphSvg.current?.select( `#${node.data.svgId}_background` )
                      .attr( 'class', ( d, i, nodeList ) => {
                          const el = nodeList[ i ] as SVGElement | undefined;

                          let retVal : string | null = null;

                          if ( el ) {
                              el.classList.remove( 'yn-graph-node-hover' );
                              retVal = el.classList.toString();
                          }

                          return retVal;
                      } );
              },
              [
                  'onlyOnce'
              ]
          ),
          onOutsideNodeClick                                        = useCallback(
              ( event : any, d : unknown ) => {
                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                  }

                  dispatch( setParam( uid, 'selectedNodes', undefined ) );

                  setFiltersMenuExpandedKeys( {} );
                  document.getElementById( `${elementId}_filters` )?.classList.remove( 'expand' );

                  setOptionMenuExpandedKeys( {} );
                  document.getElementById( `${elementId}_options` )?.classList.remove( 'expand' );
              },
              [
                  setFormPersistentSubPanelProps,
                  uid
              ]
          ),
          onZoom                                                    = useCallback(
              ( event : any ) => {
                  const svgBox               = document.getElementById( `${elementId}_svg` )?.getBoundingClientRect(),
                        zoomTargetNode       = document.getElementById( `${zoomTargetElementId}` ),
                        currentTransformAttr = zoomTargetNode?.getAttribute( 'transform' );

                  if ( event.sourceEvent ) {
                      userZoom.current = true;
                  }

                  if ( event.transform.toString() !== currentTransformAttr ) {
                      const currentTransform = new d3.ZoomTransform(
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*scale\(([0-9.]+)/, '$1' ) ) ) / 10000
                                    : 1,
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(([0-9.\-]+),.*/, '$1' ) ) ) / 10000
                                    : 0,
                                currentTransformAttr
                                    ? Math.round( 10000 * parseFloat( currentTransformAttr.replace( /.*translate\(.*,([0-9.\-]+)/, '$1' ) ) ) / 10000
                                    : 0
                            ),
                            currentBox       = zoomTargetNode?.getBoundingClientRect(),
                            k                = Math.round( 10000 * event.transform.k ) / 10000;

                      let x = event.transform.x,
                          y = event.transform.y;

                      if ( svgBox && currentTransform && currentBox?.width ) {
                          const fK      = k / currentTransform.k,
                                offsetX = svgBox.x - fK * ( currentBox.x - currentTransform.x ),
                                offsetY = svgBox.y - fK * ( currentBox.y - currentTransform.y );

                          if ( fK * currentBox.width < svgBox.width / 2 ) {
                              x = offsetX - .5 * fK * currentBox.width + .5 * svgBox.width;
                          }
                          else {
                              const minX = offsetX - fK * currentBox.width + .5 * svgBox.width,
                                    maxX = offsetX + .5 * svgBox.width;

                              if ( x < minX ) {
                                  x = minX;
                              }
                              else if ( x > maxX ) {
                                  x = maxX;
                              }
                          }

                          if ( fK * currentBox.height < svgBox.height / 2 ) {
                              y = offsetY - .5 * fK * currentBox.height + .5 * svgBox.height;
                          }
                          else {
                              const minY = offsetY - fK * currentBox.height + .5 * svgBox.height,
                                    maxY = offsetY + .5 * svgBox.height;

                              if ( y < minY ) {
                                  y = minY;
                              }
                              else if ( y > maxY ) {
                                  y = maxY;
                              }
                          }
                      }

                      x = Math.round( 10000 * x ) / 10000;
                      y = Math.round( 10000 * y ) / 10000;

                      if ( !isNaN( x ) && !isNaN( y ) && ( x !== currentTransform.x || y !== currentTransform.y || k !== currentTransform.k ) ) {
                          const transform                                = new d3.ZoomTransform(
                                    k,
                                    x,
                                    y
                                ),
                                newSvgGraphTransform : SvgGraphTransform = {
                                    x,
                                    y,
                                    k
                                };

                          graphSvg.current
                              ?.select( `#${zoomTargetElementId}` )
                              .attr( 'transform', transform.toString() );

                          if ( setSvgGraphTransformTimeoutId.current ) {
                              clearTimeout( setSvgGraphTransformTimeoutId.current );
                          }
                          setSvgGraphTransformTimeoutId.current = setTimeout(
                              () => {
                                  if ( cZoom.current ) {
                                      graphSvg.current
                                          ?.call( cZoom.current.transform, transform );
                                  }

                                  if ( setFormPersistentSubPanelProps ) {
                                      setFormPersistentSubPanelProps( 'svgGraphTransform', newSvgGraphTransform );
                                  }

                                  dispatch( setParam( uid, 'svgGraphTransform', newSvgGraphTransform ) );
                              },
                              100
                          );
                      }
                  }
              },
              [
                  setFormPersistentSubPanelProps,
                  uid
              ]
          ),
          resizeHandler                                             = useCallback(
              () => {
                  if ( resizeTimeoutId.current ) {
                      clearTimeout( resizeTimeoutId.current );
                  }

                  resizeTimeoutId.current = setTimeout(
                      setRerenderToggle.bind( null, cRenderToggle.current++ ),
                      25
                  );
              },
              [
                  'onlyOnce'
              ]
          ),
          setPresets                                                = useCallback(
              ( referenceDepth? : number ) => {
                  const referenceDepthString = String( referenceDepth ?? dataDisplayConfig.referenceDepth ) as '1' | '2' | '3' | '4';

                  if ( simulationParametersPresets[ referenceDepthString ] ) {
                      setSimulationParameters( { ...simulationParametersPresets[ referenceDepthString ] } );
                  }
              },
              [
                  dataDisplayConfig
              ]
          ),
          resizeObserver                                            = useMemo(
              () => new ResizeObserver( resizeHandler ),
              [
                  resizeHandler
              ]
          ),
          graph                                                     = useMemo(
              () => {
                  return <div
                      key={elementId}
                      id={elementId}
                      className="yn-graph"
                      style={{
                          height: '100%',
                          width : '100%'
                      }}
                  ></div>;
              },
              [
                  elementId
              ]
          ),
          filtersMenu                                               = useMemo(
              () => {
                  const menuItems : MenuItem[] = [];

                  if ( type === 'data' ) {
                      menuItems.push( {
                          label   : 'type',
                          template: () => {
                              const handleGraphTypeChange = ( newGraphType : AllProps['graphType'] ) => {

                                  svgSimulation.current?.stop();
                                  if ( setSvgNodesTransformTimeoutId.current ) {
                                      clearTimeout( setSvgNodesTransformTimeoutId.current );
                                  }

                                  if ( setFormPersistentSubPanelProps ) {
                                      setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                                      setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                                      setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                                  }

                                  dispatch( setParam( uid, 'selectedNodes', undefined ) );
                                  dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                                  dispatch( setParam( uid, 'svgNodesTransform', undefined ) );

                                  if ( onGraphTypeChange ) {
                                      onGraphTypeChange( newGraphType );
                                  }
                                  else {
                                      if ( setFormPersistentSubPanelProps ) {
                                          setFormPersistentSubPanelProps( 'graphType', newGraphType );
                                      }

                                      dispatch( setParam( uid, 'graphType', newGraphType ) );

                                      setResetViewToggle( resetViewToggle + 1 );
                                  }
                              };

                              return <div className="flex align-items-center">
                                  <span
                                      className="flex justify-content-stretch p-buttonset yn-graph-options-input"
                                      style={{
                                          margin: '.25rem 1rem .5rem 1rem',
                                          width : '100%'
                                      }}
                                  >
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::graph_7::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.graphType.force' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${graphType === 'force' ? '' : ' p-button-outlined'}`}
                                          onClick={handleGraphTypeChange.bind( null, 'force' )}
                                      />
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::graph_4::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.graphType.tree' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${graphType === 'tree' ? '' : ' p-button-outlined'}`}
                                          onClick={handleGraphTypeChange.bind( null, 'tree' )}
                                      />
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::graph_5::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.graphType.radialTree' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${graphType === 'radialTree' ? '' : ' p-button-outlined'}`}
                                          onClick={handleGraphTypeChange.bind( null, 'radialTree' )}
                                      />
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::account_tree::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.graphType.tangledTree' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${graphType === 'tangledTree' ? '' : ' p-button-outlined'}`}
                                          onClick={handleGraphTypeChange.bind( null, 'tangledTree' )}
                                      />
                                  </span>
                              </div>;
                          }
                      } );
                      if ( showReferenceDirectionFilter ) {
                          menuItems.push( {
                              label   : 'referenceDirection',
                              template: () => {
                                  const onReferenceDirectionChange = ( newReferenceDirection : typeof dataDisplayConfig.referenceDirection ) => {
                                      const newDataDisplayConfig = {
                                          ...dataDisplayConfig,
                                          referenceDirection: newReferenceDirection
                                      };

                                      svgSimulation.current?.stop();
                                      if ( setSvgNodesTransformTimeoutId.current ) {
                                          clearTimeout( setSvgNodesTransformTimeoutId.current );
                                      }

                                      if ( setFormPersistentSubPanelProps ) {
                                          setFormPersistentSubPanelProps( 'dataDisplayConfig', newDataDisplayConfig );
                                          setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                                          setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                                          setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                                      }

                                      dispatch( setParam( uid, 'dataDisplayConfig', newDataDisplayConfig ) );
                                      dispatch( setParam( uid, 'selectedNodes', undefined ) );
                                      dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                                      dispatch( setParam( uid, 'svgNodesTransform', undefined ) );

                                      dispatch( setElusiveData( uid, { dataLoaded: false } ) );
                                  };

                                  return <div className="flex align-items-center">
                                  <span
                                      className="flex justify-content-stretch p-buttonset yn-graph-options-input"
                                      style={{
                                          margin: '0 1rem',
                                          width : '100%'
                                      }}
                                  >
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::arrow_upward_alt::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.referenceDirection.srcToDest' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${dataDisplayConfig.referenceDirection === 'SrcToDest' ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDirectionChange.bind( null, 'SrcToDest' as typeof dataDisplayConfig.referenceDirection )}
                                      />
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::swap_vert::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.referenceDirection.both' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${dataDisplayConfig.referenceDirection === 'both' ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDirectionChange.bind( null, 'both' as typeof dataDisplayConfig.referenceDirection )}
                                      />
                                      <Button
                                          icon={getIconSpan(
                                              `material-symbols-outlined ::arrow_downward_alt::`,
                                              {
                                                  style: {
                                                      fontSize   : 'calc(1rem + 2px)',
                                                      width      : '12px',
                                                      height     : '12px',
                                                      marginRight: '.5rem'
                                                  }
                                              }
                                          )}
                                          tooltip={__( 'YN.base.frontend.graph.referenceDirection.destToSrc' )}
                                          tooltipOptions={config.frontend?.buttonTooltipOptions}
                                          className={`flex-auto${dataDisplayConfig.referenceDirection === 'DestToSrc' ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDirectionChange.bind( null, 'DestToSrc' as typeof dataDisplayConfig.referenceDirection )}
                                      />
                                  </span>
                                  </div>;
                              }
                          } );
                      }
                      if ( showReferenceDepthFilter ) {
                          menuItems.push( {
                              label   : 'referenceDepth',
                              template: () => {
                                  const onReferenceDepthChange = ( newReferenceDepth : number ) => {
                                      const newDataDisplayConfig = {
                                          ...dataDisplayConfig,
                                          referenceDepth: newReferenceDepth
                                      };

                                      svgSimulation.current?.stop();
                                      if ( setSvgNodesTransformTimeoutId.current ) {
                                          clearTimeout( setSvgNodesTransformTimeoutId.current );
                                      }

                                      if ( setFormPersistentSubPanelProps ) {
                                          setFormPersistentSubPanelProps( 'dataDisplayConfig', newDataDisplayConfig );
                                          setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                                          setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                                          setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                                      }

                                      dispatch( setParam( uid, 'dataDisplayConfig', newDataDisplayConfig ) );
                                      dispatch( setParam( uid, 'selectedNodes', undefined ) );
                                      dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                                      dispatch( setParam( uid, 'svgNodesTransform', undefined ) );

                                      if ( usePresets ) {
                                          setPresets( newReferenceDepth );
                                      }

                                      dispatch( setElusiveData( uid, { dataLoaded: false } ) );
                                  };

                                  return <div className="flex align-items-center">
                                  <span
                                      className="flex justify-content-stretch p-buttonset yn-graph-options-input"
                                      style={{
                                          width: '100%'
                                      }}
                                  >
                                      <Button
                                          label="1"
                                          className={`flex-auto${dataDisplayConfig.referenceDepth === 1 ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDepthChange.bind( null, 1 )}
                                      />
                                      <Button
                                          label="2"
                                          className={`flex-auto${dataDisplayConfig.referenceDepth === 2 ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDepthChange.bind( null, 2 )}
                                      />
                                      <Button
                                          label="3"
                                          className={`flex-auto${dataDisplayConfig.referenceDepth === 3 ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDepthChange.bind( null, 3 )}
                                      />
                                      <Button
                                          label="4"
                                          className={`flex-auto${dataDisplayConfig.referenceDepth === 4 ? '' : ' p-button-outlined'}`}
                                          onClick={onReferenceDepthChange.bind( null, 4 )}
                                      />
                                  </span>
                                  </div>;
                              }
                          } );
                      }
                      menuItems.push( { separator: true } );
                  }

                  menuItems.push( {
                      label  : __( 'YN.base.frontend.graph.options.resetView' ),
                      icon   : 'pi pi-replay',
                      command: () => {
                          const newHiddenNodeItemTypes : number[] = [];

                          svgSimulation.current?.stop();
                          if ( setSvgNodesTransformTimeoutId.current ) {
                              clearTimeout( setSvgNodesTransformTimeoutId.current );
                          }

                          if ( setFormPersistentSubPanelProps ) {
                              setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                              setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                              setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                              setFormPersistentSubPanelProps( 'hiddenNodeItemTypes', newHiddenNodeItemTypes );
                          }

                          dispatch( setParam( uid, 'selectedNodes', undefined ) );
                          dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                          dispatch( setParam( uid, 'svgNodesTransform', undefined ) );
                          dispatch( setParam( uid, 'hiddenNodeItemTypes', newHiddenNodeItemTypes ) );

                          setResetViewToggle( resetViewToggle + 1 );
                      }
                  } );
                  menuItems.push( {
                      label  : __( 'YN.base.frontend.graph.options.reloadView' ),
                      icon   : 'pi pi-refresh',
                      command: () => {
                          const newHiddenNodeItemTypes : number[] = [];

                          svgSimulation.current?.stop();
                          if ( setSvgNodesTransformTimeoutId.current ) {
                              clearTimeout( setSvgNodesTransformTimeoutId.current );
                          }

                          if ( setFormPersistentSubPanelProps ) {
                              setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                              setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                              setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                              setFormPersistentSubPanelProps( 'hiddenNodeItemTypes', newHiddenNodeItemTypes );
                          }

                          dispatch( setParam( uid, 'selectedNodes', undefined ) );
                          dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                          dispatch( setParam( uid, 'svgNodesTransform', undefined ) );
                          dispatch( setParam( uid, 'hiddenNodeItemTypes', newHiddenNodeItemTypes ) );
                          dispatch( setParam( uid, 'dataStructures', undefined ) );
                          dispatch( setParam( uid, 'configHash', undefined ) );

                          if ( onReloadView ) {
                              onReloadView();
                          }
                          else {
                              dispatch( setElusiveData( uid, { configLoaded: false, dataLoaded: false } ) );
                          }
                      }
                  } )
                  menuItems.push( { separator: true } );
                  menuItems.push( {
                      label  : __( 'YN.base.frontend.graph.options.fullscreen' ),
                      icon   : resolveLigatureIcon( `yn-icon material-symbols-outlined ::fullscreen${fullScreen ? '_exit' : ''}::` ),
                      command: () => {
                          const newFullScreen = !fullScreen,
                                svgElem       = document.getElementById( elementId )?.parentElement;

                          setFullScreen( newFullScreen );
                          if ( newFullScreen && svgElem ) {
                              void svgElem.requestFullscreen();
                          }
                          else {
                              void document.exitFullscreen();
                          }
                      }
                  } );
                  menuItems.push( {
                      label  : __( 'YN.base.frontend.graph.options.showTooltips' ),
                      icon   : showTooltips ? 'pi pi-eye' : 'pi pi-eye-slash',
                      command: () => {
                          if ( onShowTooltipsChange ) {
                              onShowTooltipsChange( !showTooltips );
                          }
                          else {
                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'showTooltips', !showTooltips );
                              }

                              dispatch( setParam( uid, 'showTooltips', !showTooltips ) );
                          }
                      }
                  } );

                  if ( type === 'data' ) {
                      menuItems.push( {
                          label  : __( 'YN.base.frontend.graph.options.colorItemTypes' ),
                          icon   : getIconSpan( `material-symbols-outlined ::${colorItemTypes ? 'invert_colors' : 'invert_colors_off'}::`, { style: { fontSize: 'calc(1rem + 2px)', width: '12px', height: '12px', marginRight: '.5rem' } } ),
                          command: () => {
                              if ( onColorItemTypesChange ) {
                                  onColorItemTypesChange( !colorItemTypes );
                              }
                              else {
                                  if ( setFormPersistentSubPanelProps ) {
                                      setFormPersistentSubPanelProps( 'colorItemTypes', !colorItemTypes );
                                  }

                                  dispatch( setParam( uid, 'colorItemTypes', !colorItemTypes ) );
                              }
                          }
                      } );
                  }
                  else if ( type === 'meta' ) {
                      menuItems.push( {
                          label  : __( 'YN.base.frontend.graph.options.showLinkNames', { linksLabel: __( typeof linksLabel === 'string' ? linksLabel : linksLabel.key ) } ),
                          icon   : showLinkNames ? 'pi pi-eye' : 'pi pi-eye-slash',
                          command: () => {
                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'showLinkNames', !showLinkNames );
                              }

                              dispatch( setParam( uid, 'showLinkNames', !showLinkNames ) );
                          }
                      } );
                      menuItems.push( {
                          label  : __( 'YN.base.frontend.graph.options.showLinkCardinality', { linksLabel: __( typeof linksLabel === 'string' ? linksLabel : linksLabel.key ) } ),
                          icon   : showLinkCardinality ? 'pi pi-eye' : 'pi pi-eye-slash',
                          command: () => {
                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'showLinkCardinality', !showLinkCardinality );
                              }

                              dispatch( setParam( uid, 'showLinkCardinality', !showLinkCardinality ) );
                          }
                      } );
                  }

                  return <PanelMenu
                      key={`${elementId}_filters`}
                      id={`${elementId}_filters`}
                      model={[
                          {
                              icon : Object.keys( filtersMenuExpandedKeys ?? {} ).length ? 'pi' : 'fa fa-sliders',
                              items: menuItems
                          }
                      ]}
                      className={`yn-graph-options yn-graph-options-left${fullScreen ? ' yn-graph-options-fullscreen' : ''}${Object.keys( filtersMenuExpandedKeys ).length ? ' expand' : ''}`}
                      // @ts-expect-error - ts definition wrong
                      expandedKeys={filtersMenuExpandedKeys}
                      onExpandedKeysChange={( value : any ) => {
                          setFiltersMenuExpandedKeys( value );
                      }}
                      onOpen={() => {
                          document.getElementById( `${elementId}_filters` )?.classList.add( 'expand' );
                      }}
                      onClose={() => {
                          document.getElementById( `${elementId}_filters` )?.classList.remove( 'expand' );
                      }}
                  />;
              },
              [
                  colorItemTypes,
                  dataDisplayConfig,
                  elementId,
                  filtersMenuExpandedKeys,
                  fullScreen,
                  graphType,
                  hiddenNodeItemTypes,
                  language,
                  linksLabel,
                  messageCatalogTimestamp,
                  nodeItemTypes,
                  onColorItemTypesChange,
                  onGraphTypeChange,
                  onReloadView,
                  onShowTooltipsChange,
                  resetViewToggle,
                  setFormPersistentSubPanelProps,
                  setPresets,
                  showLinkCardinality,
                  showLinkNames,
                  showReferenceDepthFilter,
                  showReferenceDirectionFilter,
                  showTooltips,
                  type,
                  uid,
                  usePresets
              ]
          ),
          optionsMenu                                               = useMemo(
              () => {
                  if ( showOptionsMenu ) {
                      const menuItems : MenuItem[] = [],
                            resetView              = () => {
                                //const newHiddenNodeItemTypes : number[] = [];

                                svgSimulation.current?.stop();
                                if ( setSvgNodesTransformTimeoutId.current ) {
                                    clearTimeout( setSvgNodesTransformTimeoutId.current );
                                }

                                if ( setFormPersistentSubPanelProps ) {
                                    //setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                                    setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                                    setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                                    //setFormPersistentSubPanelProps( 'hiddenNodeItemTypes', newHiddenNodeItemTypes );
                                }

                                //dispatch( setParam( uid, 'selectedNodes', undefined ) );
                                dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                                dispatch( setParam( uid, 'svgNodesTransform', undefined ) );
                                //dispatch( setParam( uid, 'hiddenNodeItemTypes', newHiddenNodeItemTypes ) );

                                setResetViewToggle( resetViewToggle + 1 );
                            };

                      if ( showSimulationParametersInOptionsMenu ) {
                          menuItems.push( {
                              label   : 'usePresets',
                              template: () => {
                                  return <div
                                      className="yn-graph-options-input field-checkbox"
                                  >
                                      <Checkbox
                                          inputId={`${uid}_usePresets`}
                                          value={usePresets}
                                          onChange={( event ) => {
                                              setUsePresets( !usePresets );
                                              if ( !usePresets ) {
                                                  setPresets();
                                                  resetView();
                                              }
                                          }}

                                          checked={usePresets}
                                      />
                                      <label
                                          htmlFor={`${uid}_usePresets`}
                                          style={{ order: 2 }}
                                      >
                                          Vorbelegung benutzen
                                      </label>
                                  </div>;
                              }
                          } );
                          menuItems.push( {
                              label   : 'simulationStartAlpha',
                              template: () => {
                                  return <>
                                      <div
                                          className="yn-graph-options-input-group"
                                          style={{ padding: '0' }}
                                      >
                                          <label
                                              htmlFor={`${uid}_simulationStartAlpha`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Startpunkt der Abkühlung [0,1]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_simulationStartAlpha`}
                                              value={simulationParameters.simulationStartAlpha}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      simulationStartAlpha: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={0}
                                              max={1}
                                              step={.05}
                                              minFractionDigits={0}
                                              maxFractionDigits={2}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_simulationStartAlpha_slider`}
                                              value={simulationParameters.simulationStartAlpha}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      simulationStartAlpha: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              min={0}
                                              max={1}
                                              step={.05}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                  </>;
                              }
                          } );
                          menuItems.push( {
                              label   : 'alphaDecay',
                              template: () => {
                                  return <>
                                      <div
                                          className="yn-graph-options-input-group"
                                          style={{ padding: '0' }}
                                      >
                                          <label
                                              htmlFor={`${uid}_alphaDecay`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Abkühlungs-Schrittweite [0-1]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_alphaDecay`}
                                              value={simulationParameters.alphaDecay}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      alphaDecay: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={0}
                                              max={1}
                                              step={.01}
                                              minFractionDigits={0}
                                              maxFractionDigits={2}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_alphaDecay_slider`}
                                              value={simulationParameters.alphaDecay}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      alphaDecay: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              min={0}
                                              max={1}
                                              step={.01}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                  </>;
                              }
                          } );
                          menuItems.push( {
                              label   : 'velocityDecay',
                              template: () => {
                                  return <>
                                      <div
                                          className="yn-graph-options-input-group"
                                          style={{ padding: '0', marginBottom: '1rem' }}
                                      >
                                          <label
                                              htmlFor={`${uid}_velocityDecay`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Abkühlungs-Geschwindigkeitsabfall [0-1]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_velocityDecay`}
                                              value={simulationParameters.velocityDecay}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      velocityDecay: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={0}
                                              max={1}
                                              step={.05}
                                              minFractionDigits={0}
                                              maxFractionDigits={2}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_velocityDecay_slider`}
                                              value={simulationParameters.velocityDecay}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      velocityDecay: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              min={0}
                                              max={1}
                                              step={.05}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                  </>;
                              }
                          } );
                          menuItems.push( { separator: true } );
                          menuItems.push( {
                              label   : 'manyBody',
                              template: () => {
                                  return <>
                                      <div
                                          className="yn-graph-options-input field-checkbox"
                                      >
                                          <Checkbox
                                              inputId={`${uid}_manyBody`}
                                              value={simulationParameters.manyBody}
                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBody: !simulationParameters.manyBody
                                                  } );
                                                  resetView();
                                              }}

                                              checked={simulationParameters.manyBody}
                                          />
                                          <label
                                              htmlFor={`${uid}_manyBody`}
                                              style={{ order: 2 }}
                                          >
                                              Abstoßungskraft
                                          </label>
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                      >
                                          <label
                                              htmlFor={`${uid}_manyBodyStrength`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Stärke [1-200]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_manyBodyStrength`}
                                              value={-1 * simulationParameters.manyBodyStrength}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyStrength: -1 * ( event.value ?? 0 )
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.manyBody}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={1}
                                              max={200}
                                              step={1}
                                              minFractionDigits={0}
                                              maxFractionDigits={0}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_manyBodyStrength_slider`}
                                              value={-1 * simulationParameters.manyBodyStrength}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyStrength: -1 * ( event.value as number )
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.manyBody}

                                              min={1}
                                              max={200}
                                              step={1}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                      >
                                          <label
                                              htmlFor={`${uid}_manyBodyTheta`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Theta [0.1-10]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_manyBodyTheta`}
                                              value={simulationParameters.manyBodyTheta}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyTheta: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.manyBody}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={.1}
                                              max={10}
                                              step={.1}
                                              minFractionDigits={1}
                                              maxFractionDigits={1}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_manyBodyTheta_slider`}
                                              value={simulationParameters.manyBodyTheta}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyTheta: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.manyBody}

                                              min={.1}
                                              max={10}
                                              step={.1}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                      >
                                          <label
                                              htmlFor={`${uid}_manyBodyDistanceMin`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Mindest-Abstand [0.1-10]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_manyBodyDistanceMin`}
                                              value={simulationParameters.manyBodyDistanceMin}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyDistanceMin: event.value ?? 1
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.manyBody}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={.1}
                                              max={10}
                                              step={.1}
                                              minFractionDigits={1}
                                              maxFractionDigits={1}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_manyBodyDistanceMin_slider`}
                                              value={simulationParameters.manyBodyDistanceMin}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyDistanceMin: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.manyBody}

                                              min={.1}
                                              max={10}
                                              step={.1}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                          style={{ marginBottom: '1rem' }}
                                      >
                                          <label
                                              htmlFor={`${uid}_manyBodyDistanceMax`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Maximal-Abstand [0-100000]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_manyBodyDistanceMax`}
                                              value={simulationParameters.manyBodyDistanceMax}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyDistanceMax: event.value ? event.value : undefined
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.manyBody}

                                              placeholder="Infinity"
                                              useGrouping={true}
                                              mode="decimal"
                                              min={0}
                                              max={100000}
                                              step={100}
                                              minFractionDigits={0}
                                              maxFractionDigits={0}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_manyBodyDistanceMax_slider`}
                                              value={simulationParameters.manyBodyDistanceMax ?? 100000}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      manyBodyDistanceMax: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.manyBody}

                                              min={1}
                                              max={100000}
                                              step={100}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                  </>;
                              }
                          } );
                          menuItems.push( { separator: true } );
                          menuItems.push( {
                              label   : 'linkForce',
                              template: () => {
                                  return <>
                                      <div
                                          className="yn-graph-options-input field-checkbox"
                                      >
                                          <Checkbox
                                              inputId={`${uid}_linkForce`}
                                              value={simulationParameters.linkForce}
                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      linkForce: !simulationParameters.linkForce
                                                  } );
                                                  resetView();
                                              }}

                                              checked={simulationParameters.linkForce}
                                          />
                                          <label
                                              htmlFor={`${uid}_linkForce`}
                                              style={{ order: 2 }}
                                          >
                                              Anziehungskraft
                                          </label>
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                      >
                                          <label
                                              htmlFor={`${uid}_linkDistance`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Abstand [1-1000]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_linkDistance`}
                                              value={simulationParameters.linkDistance}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      linkDistance: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.linkForce}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={1}
                                              max={1000}
                                              step={1}
                                              minFractionDigits={0}
                                              maxFractionDigits={0}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}linkDistance_slider`}
                                              value={simulationParameters.linkDistance}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      linkDistance: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.linkForce}

                                              min={1}
                                              max={1000}
                                              step={1}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                      <div
                                          className="yn-graph-options-input-group"
                                          style={{ marginBottom: '1rem' }}
                                      >
                                          <label
                                              htmlFor={`${uid}_linkStrength`}
                                              className="yn-graph-options-input-label"
                                          >
                                              Stärkegewichtung [1-3]
                                          </label>
                                          <InputNumber
                                              id={`${uid}_linkStrength`}
                                              value={simulationParameters.linkStrength}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      linkStrength: event.value ?? 0
                                                  } );
                                                  resetView();
                                              }}

                                              disabled={!simulationParameters.linkForce}

                                              useGrouping={true}
                                              mode="decimal"
                                              min={1}
                                              max={3}
                                              step={.05}
                                              minFractionDigits={2}
                                              maxFractionDigits={2}

                                              className="yn-graph-options-input-control"
                                          />
                                          <Slider
                                              id={`${uid}_linkStrength_slider`}
                                              value={simulationParameters.linkStrength}

                                              onChange={( event ) => {
                                                  setSimulationParameters( {
                                                      ...simulationParameters,
                                                      linkStrength: event.value as number
                                                  } );
                                              }}
                                              onSlideEnd={resetView}

                                              disabled={!simulationParameters.linkForce}

                                              min={1}
                                              max={3}
                                              step={.05}

                                              style={{ width: '100%' }}
                                          />
                                      </div>
                                  </>;
                              }
                          } );
                          menuItems.push( { separator: true } );
                      }

                      menuItems.push( {
                          label  : __( 'YN.base.frontend.graph.options.resetView' ),
                          icon   : 'pi pi-replay',
                          command: resetView
                      } );
                      menuItems.push( {
                          label  : __( 'YN.base.frontend.graph.options.reloadView' ),
                          icon   : 'pi pi-refresh',
                          command: () => {
                              const newHiddenNodeItemTypes : number[] = [];

                              svgSimulation.current?.stop();
                              if ( setSvgNodesTransformTimeoutId.current ) {
                                  clearTimeout( setSvgNodesTransformTimeoutId.current );
                              }

                              if ( setFormPersistentSubPanelProps ) {
                                  setFormPersistentSubPanelProps( 'selectedNodes', undefined );
                                  setFormPersistentSubPanelProps( 'svgGraphTransform', undefined );
                                  setFormPersistentSubPanelProps( 'svgNodesTransform', undefined );
                                  setFormPersistentSubPanelProps( 'hiddenNodeItemTypes', newHiddenNodeItemTypes );
                              }

                              dispatch( setParam( uid, 'selectedNodes', undefined ) );
                              dispatch( setParam( uid, 'svgGraphTransform', undefined ) );
                              dispatch( setParam( uid, 'svgNodesTransform', undefined ) );
                              dispatch( setParam( uid, 'hiddenNodeItemTypes', newHiddenNodeItemTypes ) );
                              dispatch( setParam( uid, 'dataStructures', undefined ) );
                              dispatch( setParam( uid, 'configHash', undefined ) );

                              dispatch( setElusiveData( uid, { configLoaded: false, dataLoaded: false } ) );
                          }
                      } );

                      if ( showSimulationParametersInOptionsMenu ) {
                          menuItems.push( { separator: true } );
                          menuItems.push( {
                              label  : 'Zeige Simulations-Parameter',
                              icon   : getIconSpan( 'material-symbols-outlined ::receipt::', { style: { fontSize: 'calc(1rem + 2px)', width: '12px', height: '12px', marginRight: '.5rem' } } ),
                              command: () => {
                                  setShowSimulationParameters( true );
                              }
                          } );
                      }

                      return <PanelMenu
                          key={`${elementId}_options`}
                          id={`${elementId}_options`}
                          model={[
                              {
                                  icon : Object.keys( optionMenuExpandedKeys ?? {} ).length ? 'pi' : 'fa fa-gears',
                                  items: menuItems
                              }
                          ]}
                          className={`yn-graph-options yn-graph-options-right${fullScreen ? ' yn-graph-options-fullscreen' : ''}${Object.keys( optionMenuExpandedKeys ).length ? ' expand' : ''}`}
                          // @ts-expect-error - ts definition wrong
                          expandedKeys={optionMenuExpandedKeys}
                          onExpandedKeysChange={( value : any ) => {
                              setOptionMenuExpandedKeys( value );
                          }}
                          onOpen={() => {
                              document.getElementById( `${elementId}_options` )?.classList.add( 'expand' );
                          }}
                          onClose={() => {
                              document.getElementById( `${elementId}_options` )?.classList.remove( 'expand' );
                          }}
                      />;
                  }
                  else {
                      return null;
                  }
              },
              [
                  elementId,
                  language,
                  linksLabel,
                  messageCatalogTimestamp,
                  optionMenuExpandedKeys,
                  resetViewToggle,
                  setFormPersistentSubPanelProps,
                  setPresets,
                  showOptionsMenu,
                  simulationParameters,
                  type,
                  uid,
                  usePresets
              ]
          ),
          itemTypeVisibilityAndColorLegend                          = useMemo(
              () => {
                  return <div
                      key={`${elementId}_itemTypeVisibilityAndColorLegend`}
                      id={`${elementId}_itemTypeVisibilityAndColorLegend`}
                      className="yn-graph-itemtype-visibility-color-legend"
                  >
                      {nodeItemTypes
                          .sort( ( a, b ) => a.label > b.label ? 1 : -1 )
                          .map( ( itemType, i ) => {
                              const active        = !hiddenNodeItemTypes!.includes( itemType.id ),
                                    onBlurOrOut   = () => {
                                        graphSvg.current
                                            ?.select( `#${linksId}` )
                                            .selectAll( 'path' )
                                            .attr( 'opacity', 1 );
                                        graphSvg.current
                                            ?.select( `#${nodesId}` )
                                            .selectAll( 'g' )
                                            .attr( 'opacity', 1 );
                                    },
                                    onFocusOrOver = ( isActive : boolean ) => {
                                        if ( isActive ) {
                                            graphSvg.current
                                                ?.select( `#${linksId}` )
                                                .selectAll( 'path' )
                                                .attr( 'opacity', .2 );
                                            graphSvg.current
                                                ?.select( `#${nodesId}` )
                                                .selectAll( 'g' )
                                                .attr( 'opacity', ( d ) => ( d as MetaNode ).itemTypeId === itemType.id || ( d as d3.HierarchyNode<DataNode> ).data?.itemTypeId === itemType.id ? 1 : .2 );
                                        }
                                        else {
                                            onBlurOrOut();
                                        }
                                    },
                                    onClick       = () => {
                                        let isActive                          = false,
                                            newHiddenNodeItemTypes : number[] = [];

                                        if ( hiddenNodeItemTypes.includes( itemType.id ) ) {
                                            isActive = true;
                                            newHiddenNodeItemTypes = hiddenNodeItemTypes.filter( id => id !== itemType.id );
                                        }
                                        else {
                                            newHiddenNodeItemTypes = [ ...hiddenNodeItemTypes, itemType.id ];
                                        }

                                        if ( setFormPersistentSubPanelProps ) {
                                            setFormPersistentSubPanelProps( 'hiddenNodeItemTypes', newHiddenNodeItemTypes );
                                        }

                                        dispatch( setParam( uid, 'hiddenNodeItemTypes', newHiddenNodeItemTypes ) );

                                        setTimeout(
                                            onFocusOrOver.bind( null, isActive ),
                                            15
                                        );
                                    },
                                    onKeyDown     = ( event : React.KeyboardEvent<HTMLSpanElement> ) => {
                                        if ( event.code === 'Enter' || event.code === 'Space' ) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onClick();
                                        }
                                    };

                              return <span
                                  key={`${elementId}_colorLegend_${itemType.id}`}
                                  className={`yn-graph-color-legend-item${hiddenNodeItemTypes!.includes( itemType.id ) ? ' yn-disabled' : ''}`}
                                  style={{
                                      backgroundColor: colorItemTypes
                                          ? itemTypeColorMapping.current[ itemType.id ]
                                          : 'transparent'
                                  }}
                                  role="switch"
                                  aria-checked={active}
                                  tabIndex={i + 100}
                                  onClick={onClick}
                                  onKeyDown={onKeyDown}
                                  onFocus={onFocusOrOver.bind( null, active )}
                                  onBlur={onBlurOrOut}
                                  onMouseOver={onFocusOrOver.bind( null, active )}
                                  onMouseOut={onBlurOrOut}
                              >
                                      {itemType.label}
                                  </span>;
                          } )
                      }
                  </div>;
              },
              [
                  colorItemTypes,
                  dataSchema,
                  elementId,
                  hiddenNodeItemTypes,
                  language,
                  messageCatalogTimestamp,
                  nodeItemTypes,
                  uid
              ]
          ),
          statisticsOverlay                                         = useMemo(
              () => {
                  let nodeCount = 0;

                  if ( type === 'meta' || graphType === 'force' ) {
                      nodeCount = nodes?.length ?? 0;
                  }
                  else if ( graphType === 'tree' || graphType === 'tangledTree' || graphType === 'radialTree' ) {
                      nodeCount = cFlatNodes.current?.length ?? 0;
                  }

                  return <div
                      key={`${elementId}_statistics`}
                      id={`${elementId}_statistics`}
                      className="yn-graph-statistics"
                  >
                      {__( typeof nodesLabel === 'string' ? nodesLabel : nodesLabel.key )}: {nodeCount}, {__( typeof linksLabel === 'string' ? linksLabel : linksLabel.key )}: {links?.length ?? 0}
                  </div>;
              },
              [
                  elementId,
                  language,
                  linksLabel,
                  links,
                  messageCatalogTimestamp,
                  nodesLabel,
                  nodes
              ]
          ),
          tooltip                                                   = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( showTooltips && tooltipTarget && tooltipContent ) {
                      retVal = <Tooltip
                          key={`${elementId}_tooltip`}
                          id={`${elementId}_tooltip`}
                          appendTo={document.fullscreenElement ? document.fullscreenElement as HTMLElement : undefined}
                          target={tooltipTarget}
                          showEvent="mouseover"
                          showDelay={500}
                          mouseTrack={true}
                      >
                          {tooltipContent}
                      </Tooltip>;
                  }

                  return retVal;
              },
              [
                  elementId,
                  fullScreen,
                  showTooltips,
                  tooltipContent,
                  tooltipTarget
              ]
          ),
          showSimulationParametersDialog                            = useMemo(
              () => {
                  let retVal : React.ReactNode | null = null;

                  if ( showSimulationParameters ) {
                      retVal = <Dialog
                          key={`${uid}_simulationParametersDialog`}
                          id={`${uid}_simulationParametersDialog`}

                          header="Simulations-Parameter"

                          onHide={() => {
                              setShowSimulationParameters( false );
                          }}

                          visible={true}
                          focusOnShow={false}
                          closeOnEscape={true}

                          className="yn-form-dialog"
                          style={{
                              width            : '40rem',
                              minWidth         : '40rem',
                              [ '--min-width' ] : '40rem',
                              height           : '24.125rem',
                              minHeight        : '24.125rem',
                              [ '--minHeight' ]: '24.125rem'
                          }}
                      >
                          <textarea
                              className="yn-flex-layout-child yn-code-textarea"
                              value={JSON.stringify( simulationParameters, null, 4 )}
                              onChange={() => {
                              }}
                          />
                      </Dialog>
                  }

                  return retVal;
              },
              [
                  uid,
                  simulationParameters,
                  showSimulationParameters
              ]
          );

    cNodes.current = nodes;
    cLinks.current = links;
    cSelectedNodes.current = selectedNodes ?? [];
    cRenderToggle.current = rerenderToggle;
    cGraphDimensions.current = {
        width : graphWidth,
        height: graphHeight
    };

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    // remove observed element from resizeObserver
    useEffect(
        () => {
            document.addEventListener( 'fullscreenchange', onFullscreenChange );

            if ( usePresets ) {
                setPresets();
            }

            // unmount
            return () => {
                document.removeEventListener( 'fullscreenchange', onFullscreenChange );

                if ( observedGraphParentElement.current ) {
                    resizeObserver.unobserve( observedGraphParentElement.current );
                }

                dispatch( cancelRequests( uid ) );

                svgSimulation.current?.stop();
                if ( setSvgGraphTransformTimeoutId.current ) {
                    clearTimeout( setSvgGraphTransformTimeoutId.current );
                }
                if ( setSvgNodesTransformTimeoutId.current ) {
                    clearTimeout( setSvgNodesTransformTimeoutId.current );
                }
            }
        },
        []
    );

    useEffect(
        () => {
            if (
                !fullScreen
                && graphParentElement
                && graphParentElement.clientWidth > 0
                && graphParentElement.clientWidth === document.body.clientWidth
            ) {
                setRerenderToggle( rerenderToggle + 1 );
            }
        },
        [
            fullScreen
        ]
    );

    // ( on update )
    // load config and data if / graphHeight and graphWidth / initialize graph
    useEffect(
        () => {
            if ( viewKey || isGenericGraph ) {

                // load config and data if
                if ( !isLoading.current ) {
                    if ( !isConfigLoaded ) {
                        if ( isGenericGraph ) {
                            setGenericGraphConfig();
                        }
                        else {
                            void loadConfig();
                        }
                    }
                    else if ( !isDataLoaded && type === 'data' ) {
                        void load();
                    }
                }

                // update graphHeight and graphWidth
                if ( graphElement && graphParentElement ) {
                    if (
                        graphWidth !== graphParentElement.clientWidth
                        && graphParentElement.clientWidth > 0
                        && (
                            fullScreen
                            || graphParentElement.clientWidth !== document.body.clientWidth
                        )
                    ) {
                        setGraphWidth( graphParentElement.clientWidth );
                    }
                    if (
                        graphHeight !== graphParentElement.clientHeight
                        && graphParentElement.clientHeight > 0
                        && (
                            fullScreen
                            || graphParentElement.clientHeight !== document.body.clientHeight
                        )
                    ) {
                        setGraphHeight( graphParentElement.clientHeight );
                    }

                    if (
                        !fullScreen
                        && graphParentElement.clientWidth > 0
                        && graphParentElement.clientWidth === document.body.clientWidth
                    ) {
                        setRerenderToggle( rerenderToggle + 1 );
                    }

                    // initialize graph
                    if ( isConfigLoaded && !graphSvg.current ) {
                        hasRendered.current = true;

                        const globalStyle = window.getComputedStyle( document.documentElement ),
                              svg         = d3.select( graphElement )
                                  .append( 'svg' )
                                  .attr( 'id', `${elementId}_svg` )
                                  .style( 'font', globalStyle.getPropertyValue( 'font' ) )
                                  .attr( 'viewBox', [ 0, 0, graphElement.clientWidth, graphElement.clientHeight ] )
                                  .attr( 'width', graphElement.clientWidth )
                                  .attr( 'height', graphElement.clientHeight )
                                  .on( 'click', onOutsideNodeClick )
                                  .append( 'g' );

                        // add shadow filters
                        const filters = svg.append( 'filter' )
                            .attr( 'id', shadowFilterId )
                            .attr( 'height', '400%' )
                            .attr( 'width', '400%' )
                            .attr( 'x', '-150%' )
                            .attr( 'y', '-150%' );

                        filters.append( 'feGaussianBlur' )
                            .attr( 'in', 'BackgroundImage' )
                            .attr( 'stdDeviation', '2' )
                            .attr( 'result', 'penumbraBlur' );

                        filters.append( 'feOffset' )
                            .attr( 'in', 'penumbraBlur' )
                            .attr( 'dx', '1' )
                            .attr( 'dy', '2' )
                            .attr( 'result', 'penumbraBlurOffset' );

                        filters.append( 'feComponentTransfer' )
                            .attr( 'in', 'penumbraBlurOffset' )
                            .append( 'feFuncA' )
                            .attr( 'type', 'linear' )
                            .attr( 'slope', '1' )//'0.37' )
                            .attr( 'result', 'penumbra' );

                        filters.append( 'feMerge' )
                            .append( 'feMergeNode' )
                            .attr( 'in', 'penumbra' );

                        filters.append( 'feMergeNode' )
                            .attr( 'in', 'SourceGraphic' );

                        // add marker defs (arrow head)
                        const defs = svg.append( 'defs' )
                            .attr( 'id', `${elementId}_defs` );

                        defs.append( 'marker' )
                            .attr( 'id', arrowMarkerId )
                            .attr( 'class', 'yn-graph-arrow-marker' )
                            .attr( 'viewBox', '0 -6 18 12' )
                            .attr( 'refX', 15 )
                            .attr( 'refY', 0 )
                            .attr( 'markerWidth', 9 )
                            .attr( 'markerHeight', 6 )
                            .attr( 'orient', 'auto-start-reverse' )
                            .append( 'path' )
                            .attr( 'd', 'M 0 -6 L 18 0 L 0 6 z' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_shadow` )
                            .attr( 'class', 'yn-graph-arrow-marker-shadow' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_hover` )
                            .attr( 'class', 'yn-graph-arrow-marker-hover' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_src` )
                            .attr( 'class', 'yn-graph-arrow-marker-src' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_dest` )
                            .attr( 'class', 'yn-graph-arrow-marker-dest' );

                        defs.select( `#${arrowMarkerId}` )
                            .clone( true )
                            .attr( 'id', `${arrowMarkerId}_circle` )
                            .attr( 'class', 'yn-graph-arrow-marker-circle' );

                        graphSvg.current = svg.append( 'g' );

                        // add background and outside-click handler
                        graphSvg.current
                            .append( 'rect' )
                            .attr( 'id', backgroundElementId )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'width', '100%' )
                            .attr( 'height', '100%' )
                            .on( 'click', onOutsideNodeClick );

                        graphSvg.current
                            .append( 'g' )
                            .attr( 'id', zoomTargetElementId )
                            .attr( 'class', 'yn-svg-no-transition' );

                        // zoom handler
                        cZoom.current = d3.zoom<SVGGElement, unknown>()
                            .extent( [ [ 0, 0 ], [ graphElement.clientWidth, graphElement.clientHeight ] ] )
                            .scaleExtent( [ .1, 10 ] )
                            .on( 'zoom', onZoom );

                        graphSvg.current
                            .call( cZoom.current )
                            .on( 'dblclick.zoom', null );

                        // restore last zoom state if
                        if ( svgGraphTransform ) {
                            const transform = new d3.ZoomTransform(
                                svgGraphTransform.k ?? 1,
                                svgGraphTransform.x ?? 0,
                                svgGraphTransform.y ?? 0
                            );

                            graphSvg.current
                                .select( `#${zoomTargetElementId}` )
                                .attr( 'transform', transform.toString() );

                            graphSvg.current
                                .call( cZoom.current.transform, transform );
                        }
                    }
                }
            }
        }
    );

    // update nodes and links
    useEffect(
        () => {
            if ( isConfigLoaded && isDataLoaded ) {
                const zoomTargetElement = graphSvg.current?.select( `#${zoomTargetElementId}` );

                // links common
                if ( !zoomTargetElement?.select( `#${linksId}` ).data().length ) {
                    zoomTargetElement
                        ?.append( 'g' )
                        .attr( 'id', linksId );
                }

                zoomTargetElement
                    ?.select( `#${linksId}` )
                    .selectAll( 'g' )
                    .remove();
                zoomTargetElement
                    ?.select( `#${linksId}` )
                    .selectAll( 'path' )
                    .remove();

                // nodes common
                if ( !zoomTargetElement?.select( `#${nodesId}_bg` ).data().length ) {
                    zoomTargetElement
                        ?.append( 'g' )
                        .attr( 'id', `${nodesId}_bg` );
                }

                zoomTargetElement
                    ?.select( `#${nodesId}_bg` )
                    .selectAll( 'g' )
                    .remove();

                if ( !zoomTargetElement?.select( `#${nodesId}` ).data().length ) {
                    zoomTargetElement
                        ?.append( 'g' )
                        .attr( 'id', nodesId );
                }

                zoomTargetElement
                    ?.select( `#${nodesId}` )
                    .selectAll( 'g' )
                    .remove();

                if ( type === 'meta' || graphType === 'force' ) {
                    const nodeWidth  = 36,
                          nodeHeight = 36;

                    // links
                    const svgLinksBase = zoomTargetElement
                              ?.select( `#${linksId}` )
                              .selectAll( 'g' )
                              .data( links ),
                          svgLinkBgs   = svgLinksBase?.join( 'g' )
                              .attr( 'id', d => `${d.svgId}_bg` )
                              .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, MetaLink, d3.BaseType, unknown> | undefined,
                          svgLinks     = svgLinksBase?.join( 'g' )
                              .attr( 'id', d => d.svgId )
                              .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, MetaLink, d3.BaseType, unknown> | undefined;

                    // handler
                    svgLinks?.on( 'click', onMetaLinkClick )
                        .on( 'mouseover', onMetaLinkOver )
                        .on( 'mouseout', onMetaLinkOut );

                    // path-bg
                    svgLinkBgs?.append( 'path' )
                        .attr( 'id', d => `${d.svgId}_bg` )
                        .on( 'click', onMetaLinkClick )
                        .on( 'mouseover', onMetaLinkOver )
                        .on( 'mouseout', onMetaLinkOut )
                        .attr( 'class', 'yn-svg-no-transition yn-graph-fill-none yn-graph-stroke-backgroundColor' )
                        .attr( 'cursor', 'pointer' )
                        .attr( 'opacity', '1' )
                        .attr( 'stroke-width', 10 )
                        .lower();

                    // path
                    svgLinks?.append( 'path' )
                        .attr( 'id', d => `${d.svgId}_path` )
                        .attr( 'class', 'yn-graph-fill-none yn-graph-stroke-linkColor' )
                        .attr( 'stroke-width', 2 )
                        .attr( 'opacity', '1' )
                        .attr( 'marker-end', `url(#${arrowMarkerId})` );

                    // label
                    svgLinks?.append( 'text' )
                        .attr( 'id', d => `${d.svgId}_label` )
                        .attr( 'class', 'yn-graph-fill-textColor' )
                        .attr( 'opacity', '1' )
                        .attr( 'text-anchor', 'middle' )
                        .attr( 'dominant-baseline', 'middle' )
                        .attr( 'style', 'font-size: 1em' )
                        .attr( 'visibility', showLinkNames ? 'visible' : 'hidden' )
                        .raise()
                        .append( 'textPath' )
                        .attr( 'startOffset', '50%' )
                        .attr( 'href', d => `#${d.svgId}_path` )
                        .text( d => d.label )
                        .clone( true )
                        .attr( 'id', d => `${d.svgId}_label_background` )
                        .attr( 'class', 'yn-svg-no-transition  yn-graph-stroke-backgroundColor' )
                        .attr( 'stroke-width', 5 )
                        .lower();

                    // cardinality
                    if ( type === 'meta' ) {
                        svgLinks?.append( 'text' )
                            .attr( 'id', d => `${d.svgId}_cardinality_src` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .attr( 'style', 'font-size: 1.2rem' )
                            .attr( 'visibility', showLinkCardinality ? 'visible' : 'hidden' )
                            .raise()
                            .append( 'textPath' )
                            .attr( 'dominant-baseline', 'middle' )
                            .attr( 'text-anchor', 'start' )
                            .attr( 'startOffset', '2rem' )
                            .attr( 'href', d => `#${d.svgId}_path` )
                            .text( d => ( d as MetaLink ).sourceCardinality )
                            .clone( true )
                            .attr( 'id', d => `${d.svgId}_cardinality_src_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor yn-graph-stroke-backgroundColor' )
                            .attr( 'stroke-width', 5 )
                            .lower();
                        svgLinks?.append( 'text' )
                            .attr( 'id', d => `${d.svgId}_cardinality_dest` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .attr( 'style', 'font-size: 1.2rem' )
                            .attr( 'visibility', showLinkCardinality ? 'visible' : 'hidden' )
                            .raise()
                            .append( 'textPath' )
                            .attr( 'dominant-baseline', 'middle' )
                            .attr( 'text-anchor', 'end' )
                            .attr( 'startOffset', 'calc(100% - 2rem)' )
                            .attr( 'href', d => `#${d.svgId}_path` )
                            .text( d => ( d as MetaLink ).targetCardinality )
                            .clone( true )
                            .attr( 'id', d => `${d.svgId}_cardinality_src_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor yn-graph-stroke-backgroundColor' )
                            .attr( 'stroke-width', 5 )
                            .lower();
                    }

                    // nodes
                    const svgNodesBgBase = zoomTargetElement
                              ?.select( `#${nodesId}_bg` )
                              .attr( 'opacity', .6 )
                              .selectAll( 'g' )
                              .data( nodes as MetaNode[] ),
                          svgNodesBase   = zoomTargetElement
                              ?.select( `#${nodesId}` )
                              .selectAll( 'g' )
                              .data( nodes as MetaNode[] ),
                          svgNodes       = svgNodesBase?.join( 'g' )
                              .attr( 'id', d => d.svgId )
                              .attr( 'class', 'yn-node' )
                              .attr( 'opacity', '1' )
                              .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, MetaNode, d3.BaseType, unknown> | undefined;

                    // handler
                    svgNodes?.on( 'click', onMetaNodeClick )
                        .on( 'dblclick', onMetaNodeDblClick )
                        .on( 'mouseover', onMetaNodeOver )
                        .on( 'mouseout', onMetaNodeOut )
                        .call(
                            d3.drag<SVGGElement, MetaNode>()
                                .on( 'start', onMetaNodeDragStart )
                                .on( 'drag', onMetaNodeDrag )
                                .on( 'end', onMetaNodeDragEnd )
                        );

                    // icon
                    svgNodes?.append( 'foreignObject' )
                        .attr( 'id', d => `${d.svgId}_icon` )
                        .attr( 'x', iconNodeOnly && type === 'data'
                            ? 2
                            : 6 )
                        .attr( 'y', 6 )
                        .attr( 'width', 32 )
                        .attr( 'height', 24 )
                        .append( 'xhtml:div' )
                        .attr( 'xmlns', 'http://www.w3.org/1999/xhtml' )
                        .attr( 'class', d => `yn-graph-node-icon ${d.icon}` );

                    // label
                    // label below node
                    if ( iconNodeOnly && type === 'data' ) {
                        const label      = svgNodesBase?.join( 'g' )
                                  .attr( 'id', d => `${d.svgId}_label` )
                                  .attr( 'class', 'yn-node-label yn-node-label-text' )
                                  .lower() as d3.Selection<SVGGElement, MetaNode, d3.BaseType, unknown> | undefined,
                              svgNodesBg = svgNodesBgBase?.join( 'g' )
                                  .attr( 'id', d => `${d.svgId}_label_background` )
                                  .attr( 'class', 'yn-node-label yn-node-label-background' ) as d3.Selection<SVGGElement, MetaNode, d3.BaseType, unknown> | undefined;

                        label?.append( 'text' )
                            .attr( 'id', d => `${d.svgId}_label_text` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .text( d => d.label )
                            .attr( 'x', ( d, i, nodeList ) => nodeWidth / 2 - ( nodeList[ i ] as SVGGElement )?.getBBox().width / 2 )
                            .attr( 'y', nodeHeight + 22 );
                        label?.append( 'rect' )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'x', ( d, i, nodeList ) => nodeWidth / 2 - ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().width / 2 - 6 )
                            .attr( 'y', ( d, i, nodeList ) => nodeHeight + 6 )
                            .attr( 'width', ( d, i, nodeList ) => ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().width + 12 )
                            .attr( 'height', ( d, i, nodeList ) => ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().height + 6 )
                            .attr( 'rx', 3 )
                            .attr( 'opacity', 0 )
                            .lower();

                        svgNodesBg?.append( 'text' )
                            .text( d => d.label )
                            .attr( 'opacity', '0' )
                            .attr( 'x', ( d, i, nodeList ) => nodeWidth / 2 - ( nodeList[ i ] as SVGGElement )?.getBBox().width / 2 )
                            .attr( 'y', nodeHeight + 22 );
                        svgNodesBg?.append( 'rect' )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'x', ( d, i, nodeList ) => nodeWidth / 2 - ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().width / 2 - 6 )
                            .attr( 'y', ( d, i, nodeList ) => nodeHeight + 6 )
                            .attr( 'width', ( d, i, nodeList ) => ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().width + 12 )
                            .attr( 'height', ( d, i, nodeList ) => ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().height + 6 )
                            .attr( 'rx', 3 );
                    }
                    // label within node
                    else {
                        svgNodes?.append( 'text' )
                            .attr( 'id', d => `${d.svgId}_label` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .text( d => d.label )
                            .attr( 'x', 42 )
                            .attr( 'y', 22 );
                    }

                    // node background
                    svgNodes?.append( 'rect' )
                        .lower()
                        .attr( 'id', d => `${d.svgId}_background` )
                        .attr( 'x', 0 )
                        .attr( 'y', 0 )
                        .attr( 'width', ( d, i, nodeList ) => {
                            const width = iconNodeOnly && type === 'data'
                                ? nodeWidth
                                : ( nodeList[ i ]?.parentNode as SVGGElement )?.getBBox().width + 24;

                            d.width = width;

                            return width;
                        } )
                        .attr( 'height', ( d ) => {
                            d.height = nodeHeight;

                            return nodeHeight;
                        } )
                        .attr( 'rx', 6 )
                        .attr( 'class', d => {
                            const retVal : string[] = [];

                            if ( d.isRoot ) {
                                retVal.push( 'yn-graph-root-node' );
                            }
                            else {
                                retVal.push( 'yn-graph-node' );
                            }

                            if ( type === 'meta' ) {
                                retVal.push( 'yn-svg-no-transition' );
                            }

                            if ( cSelectedNodes.current?.includes( d.id ) ) {
                                retVal.push( 'yn-graph-active-node' );
                            }

                            return retVal.join( ' ' );
                        } )
                        .attr( 'style', d => !d.isRoot && colorItemTypes && d.color
                            ? `fill: ${d.color}`
                            : null
                        )
                        .attr( 'stroke-width', 2 )
                        .clone( true )
                        .lower()
                        .attr( 'id', d => `${d.svgId}_color_background` )
                        .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                        .attr( 'style', null )
                        .clone( true )
                        .lower()
                        .attr( 'id', d => `${d.svgId}_shadow_background` )
                        .attr( 'class', 'yn-svg-no-transition yn-graph-fill-dropShadowColor yn-graph-stroke-dropShadowColor' )
                        .attr( 'stroke-width', 3 )
                        .attr( 'filter', `url(#${shadowFilterId})` );

                    // apply position fix if
                    for ( const n of nodes as MetaNode[] ) {
                        if ( n.fixPositions ) {
                            n.x = n.x - n.width / 2;
                            n.y = n.y - n.height / 2;
                            n.fixPositions = false;
                        }
                    }


                    // simulation
                    svgNodes?.attr( 'x', d => d.x )
                        .attr( 'y', d => d.y )
                        .attr( 'transform', d => `translate( ${d.x}, ${d.y} )` );

                    if ( type === 'meta' ) {
                        svgSimulation.current = d3.forceSimulation( nodes as MetaNode[] )
                            .force(
                                'charge',
                                d3.forceManyBody()
                                    .strength( 50 )
                                    .theta( .9 )
                                    .distanceMin( 1 )
                            )
                            .force(
                                'link',
                                d3.forceLink( links ).id( d => ( d as MetaNode ).id )
                                    .distance( 400 )
                                    .strength( d => 1 / Math.pow( Math.min( ( d.source as unknown as MetaNode ).uniqueLinkCount, ( d.target as unknown as MetaNode ).uniqueLinkCount ), 2.3 ) )
                            )
                            .force(
                                'collide',
                                d3.forceCollide( d => ( d as MetaNode ).width / 2 + 25 )
                            )
                            .force(
                                'center',
                                d3.forceCenter( graphWidth / 2, graphHeight / 2 )
                                    .strength( 1 )
                            )
                            .on( 'tick', onSimulationTick )
                            .stop();
                    }
                    else {
                        svgSimulation.current = d3.forceSimulation( nodes as MetaNode[] )
                            .alpha( simulationParameters.simulationStartAlpha )
                            .alphaDecay( simulationParameters.alphaDecay )
                            .velocityDecay( simulationParameters.velocityDecay )
                            .force(
                                'collide',
                                d3.forceCollide( d => iconNodeOnly
                                    ? 2 * nodeWidth
                                    : ( d as MetaNode ).width / 2 + 25
                                )
                            )
                            .force(
                                'center',
                                d3.forceCenter( graphWidth / 2, graphHeight / 2 )
                            )
                            .on( 'tick', onSimulationTick )
                            .stop();

                        if ( simulationParameters.manyBody ) {
                            const force = d3.forceManyBody()
                                .strength( simulationParameters.manyBodyStrength )
                                .theta( simulationParameters.manyBodyTheta )
                                .distanceMin( simulationParameters.manyBodyDistanceMin );

                            if ( simulationParameters.manyBodyDistanceMax ) {
                                force.distanceMax( simulationParameters.manyBodyDistanceMax );
                            }

                            svgSimulation.current.force(
                                'charge',
                                force
                            );
                        }

                        if ( simulationParameters.linkForce ) {
                            svgSimulation.current.force(
                                'link',
                                d3.forceLink( links ).id( d => ( d as MetaNode ).id )
                                    .distance( simulationParameters.linkDistance )
                                    .strength( d => 1 / Math.pow( Math.min( ( d.source as unknown as MetaNode ).uniqueLinkCount, ( d.target as unknown as MetaNode ).uniqueLinkCount ), simulationParameters.linkStrength ) )
                            );
                        }
                    }


                    if ( layout !== 'cise' && !Object.keys( svgNodesTransform ?? {} ).length ) {
                        userZoom.current = false;
                        svgSimulation.current?.restart();
                    }
                    else {
                        svgLinks?.selectAll( 'path' )
                            .attr( 'd', drawLinks );

                        onSimulationTick();
                        fixMinZoomLevel();
                        positionNodeLabels();
                    }

                    if ( selectedNodes?.length ) {
                        window.setTimeout(
                            selectNodes.bind( null, selectedNodes ),
                            11
                        );
                    }
                }
                else if ( nodes.length ) {
                    const nodeWidth  = 250,
                          nodeHeight = 36;

                    zoomTargetElement?.attr( 'opacity', 0 );

                    if ( graphType === 'tree' ) {
                        const root = d3.hierarchy( nodes[ 0 ] as DataNode ),
                              tree = d3.tree().nodeSize( [ nodeHeight + 6, nodeWidth + 150 ] ) as d3.TreeLayout<DataNode>;

                        let sourceId : number,
                            x0 = Infinity,
                            x1 = -x0;

                        tree( root );

                        root.each( d => {
                            if ( d.x && d.x > x1 ) {
                                x1 = d.x;
                            }
                            if ( d.x && d.x < x0 ) {
                                x0 = d.x;
                            }
                        } );

                        // links
                        const svgLinks = zoomTargetElement
                            ?.select( `#${linksId}` )
                            .selectAll( 'g' )
                            .append( 'g' )
                            .data( root.links() );

                        // path-bg
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => `${d.target.data.svgId}_bg` )
                            .on( 'click', onDataLinkClick )
                            .on( 'mouseover', onDataLinkOver )
                            .on( 'mouseout', onDataLinkOut )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-none yn-graph-stroke-backgroundColor' )
                            .attr( 'stroke-width', 10 )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'opacity', '1' )
                            .attr( 'd', ( d3.linkHorizontal() as unknown as d3.Link<any, d3.HierarchyLink<DataNode>, d3.HierarchyNode<DataNode>> )
                                .source( link => {
                                    sourceId = link.source.data.id;

                                    return link.source;
                                } )
                                .x( node => {
                                    return sourceId && sourceId === node.data.id
                                        ? ( node.y ?? 0 ) + nodeWidth
                                        : ( node.y ?? 0 );
                                } )
                                .y( node => ( node.x ?? 0 ) + 18 ) )
                            .lower();

                        // path
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => `${d.target.data.svgId}_path` )
                            .on( 'click', onDataLinkClick )
                            .on( 'mouseover', onDataLinkOver )
                            .on( 'mouseout', onDataLinkOut )
                            .attr( 'class', 'link yn-graph-fill-none yn-graph-stroke-linkColor' )
                            .attr( 'stroke-width', 2 )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'd', ( d3.linkHorizontal() as unknown as d3.Link<any, d3.HierarchyLink<DataNode>, d3.HierarchyNode<DataNode>> )
                                .source( link => {
                                    sourceId = link.source.data.id;

                                    return link.source;
                                } )
                                .x( node => {
                                    return sourceId && sourceId === node.data.id
                                        ? ( node.y ?? 0 ) + nodeWidth
                                        : ( node.y ?? 0 );
                                } )
                                .y( node => ( node.x ?? 0 ) + 18 ) );

                        // nodes
                        const svgNodes = zoomTargetElement
                            ?.select( `#${nodesId}` )
                            .selectAll( 'g' )
                            .data( root.descendants() )
                            .join( 'g' )
                            .attr( 'id', d => d.data.svgId )
                            .attr( 'transform', d => `translate(${d.y},${d.x})` )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, d3.HierarchyNode<DataNode>, d3.BaseType, unknown> | undefined;

                        // handler
                        svgNodes?.on( 'click', ( event : any, node ) => {
                                onDataNodeClick( event, node.data )
                            } )
                            .on( 'dblclick', ( event : any, node ) => {
                                onDataDoubleClick( event, node.data )
                            } )
                            .on( 'mouseover', onDataNodeOver )
                            .on( 'mouseout', onDataNodeOut );

                        // icon
                        svgNodes?.append( 'foreignObject' )
                            .attr( 'id', d => `${d.data.svgId}_icon` )
                            .attr( 'x', 12 )
                            .attr( 'y', 6 )
                            .attr( 'width', 24 )
                            .attr( 'height', 24 )
                            .append( 'xhtml:div' )
                            .attr( 'xmlns', 'http://www.w3.org/1999/xhtml' )
                            .attr( 'class', d => `yn-graph-node-icon ${d.data.icon}` );

                        // label
                        svgNodes?.append( 'svg' )
                            .attr( 'x', 42 )
                            .attr( 'y', 0 )
                            .attr( 'width', nodeWidth - 52 )
                            .attr( 'height', 30 )
                            .append( 'text' )
                            .attr( 'id', d => `${d.data.svgId}_label` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .text( d => d.data.label )
                            .attr( 'x', 0 )
                            .attr( 'y', 22 );

                        // background
                        svgNodes?.append( 'rect' )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_background` )
                            .attr( 'x', 0 )
                            .attr( 'y', 0 )
                            .attr( 'width', nodeWidth )
                            .attr( 'height', nodeHeight )
                            .attr( 'rx', 6 )
                            .attr( 'class', 'yn-graph-node' )
                            .attr( 'style', d => colorItemTypes && d.data.color
                                ? `fill: ${d.data.color}`
                                : null
                            )
                            .attr( 'stroke-width', 2 )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_color_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'style', null )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_shadow_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-dropShadowColor yn-graph-stroke-dropShadowColor' )
                            .attr( 'stroke-width', 3 )
                            .attr( 'filter', `url(#${shadowFilterId})` );
                    }
                    else if ( graphType === 'radialTree' ) {
                        const tree = d3.tree()
                                  .nodeSize( [ nodeHeight + 6, nodeWidth + 150 ] )
                                  .size( [ 2 * Math.PI, dataDisplayConfig.referenceDepth * 400 ] )
                                  .separation( ( a, b ) => ( a.parent === b.parent ? 1 : 2 ) / a.depth ) as d3.TreeLayout<DataNode>,
                              root = tree( d3.hierarchy( nodes[ 0 ] as DataNode ) );

                        // links
                        const svgLinks = zoomTargetElement
                            ?.select( `#${linksId}` )
                            .selectAll( 'g' )
                            .append( 'g' )
                            .data( root.links() );

                        // path-bg
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => `${d.target.data.svgId}_bg` )
                            .on( 'click', onDataLinkClick )
                            .on( 'mouseover', onDataLinkOver )
                            .on( 'mouseout', onDataLinkOut )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-none yn-graph-stroke-backgroundColor' )
                            .attr( 'stroke-width', 10 )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'd', ( d3.linkRadial() as unknown as d3.LinkRadial<any, d3.HierarchyLink<DataNode>, d3.HierarchyNode<DataNode>> )
                                .angle( d => d.x ?? 0 )
                                .radius( d => d.y ?? 0 ) )
                            .lower();

                        // path
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => `${d.target.data.svgId}_path` )
                            .on( 'click', onDataLinkClick )
                            .on( 'mouseover', onDataLinkOver )
                            .on( 'mouseout', onDataLinkOut )
                            .attr( 'class', 'link yn-graph-fill-none yn-graph-stroke-linkColor' )
                            .attr( 'stroke-width', 2 )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'd', ( d3.linkRadial() as unknown as d3.LinkRadial<any, d3.HierarchyLink<DataNode>, d3.HierarchyNode<DataNode>> )
                                .angle( d => d.x ?? 0 )
                                .radius( d => d.y ?? 0 ) );

                        // nodes
                        const svgNodesBgBase = zoomTargetElement
                                  ?.select( `#${nodesId}_bg` )
                                  .attr( 'opacity', 1 )
                                  .selectAll( 'g' )
                                  .data( root.descendants() ),
                              svgNodesBase   = zoomTargetElement
                                  ?.select( `#${nodesId}` )
                                  .selectAll( 'g' )
                                  .data( root.descendants() ),
                              svgNodes       = svgNodesBase?.join( 'g' )
                                  .attr( 'id', d => d.data.svgId )
                                  .attr( 'class', 'yn-node' )
                                  .attr( 'opacity', '1' )
                                  .attr( 'transform', d => `rotate(${d.x * 180 / Math.PI - 90}) translate(${d.x >= Math.PI ? d.y + nodeWidth : d.y},${d.x >= Math.PI ? nodeHeight / 2 : -nodeHeight / 2}) rotate(${d.x >= Math.PI ? 180 : 0})` )
                                  .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, d3.HierarchyNode<DataNode>, d3.BaseType, unknown> | undefined;

                        // handler
                        svgNodes?.on( 'click', ( event : any, node ) => {
                                onDataNodeClick( event, node.data )
                            } )
                            .on( 'dblclick', ( event : any, node ) => {
                                onDataDoubleClick( event, node.data )
                            } )
                            .on( 'mouseover', onDataNodeOver )
                            .on( 'mouseout', onDataNodeOut );

                        // icon
                        svgNodes?.append( 'foreignObject' )
                            .attr( 'id', d => `${d.data.svgId}_icon` )
                            .attr( 'x', 12 )
                            .attr( 'y', 6 )
                            .attr( 'width', 24 )
                            .attr( 'height', 24 )
                            .append( 'xhtml:div' )
                            .attr( 'xmlns', 'http://www.w3.org/1999/xhtml' )
                            .attr( 'class', d => `yn-graph-node-icon ${d.data.icon}` );

                        // label
                        svgNodes?.append( 'svg' )
                            .attr( 'x', 42 )
                            .attr( 'y', 0 )
                            .attr( 'width', nodeWidth - 52 )
                            .attr( 'height', 30 )
                            .append( 'text' )
                            .attr( 'id', d => `${d.data.svgId}_label` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .text( d => d.data.label )
                            .attr( 'x', 0 )
                            .attr( 'y', 22 );

                        // link covering background
                        svgNodesBgBase?.join( 'g' )
                            .attr( 'id', d => d.data.svgId )
                            .attr( 'opacity', '1' )
                            .attr( 'transform', d => `rotate(${d.x * 180 / Math.PI - 90}) translate(${d.x >= Math.PI ? d.y + nodeWidth : d.y},${d.x >= Math.PI ? nodeHeight / 2 : -nodeHeight / 2}) rotate(${d.x >= Math.PI ? 180 : 0})` )
                            .append( 'rect' )
                            .attr( 'id', d => `${d.data.svgId}_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'x', 0 )
                            .attr( 'y', 0 )
                            .attr( 'width', nodeWidth )
                            .attr( 'height', nodeHeight )
                            .attr( 'rx', 6 );

                        // background
                        svgNodes?.append( 'rect' )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_background` )
                            .attr( 'x', 0 )
                            .attr( 'y', 0 )
                            .attr( 'width', nodeWidth )
                            .attr( 'height', nodeHeight )
                            .attr( 'rx', 6 )
                            .attr( 'class', 'yn-graph-node' )
                            .attr( 'style', d => colorItemTypes && d.data.color
                                ? `fill: ${d.data.color}`
                                : null
                            )
                            .attr( 'stroke-width', 2 )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_color_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'style', null )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.data.svgId}_shadow_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-dropShadowColor yn-graph-stroke-dropShadowColor' )
                            .attr( 'stroke-width', 3 )
                            .attr( 'filter', `url(#${shadowFilterId})` );
                    }
                    else if ( graphType === 'tangledTree' ) {
                        const nodePadding                      = 6,
                              nodeLinkPadding                  = 6,
                              linkPadding                      = 6,
                              bundleWidth                      = 18,
                              linkCornerRadius                 = 12,
                              color                            = d3.scaleOrdinal( d3.schemeDark2 ),
                              flatNodes : PositionedDataNode[] = [],
                              flatLinks : PositionedDataLink[] = [],
                              levelBundles : Bundle[][]        = [],
                              flatBundles : Bundle[]           = [];

                        let xOffset = 0,
                            yOffset = 0;

                        // build flatNodes, flatLinks, levelBundles and flatBundles
                        for ( let level = 0; level < ( nodes as DataNode[][] ).length; level++ ) {
                            const bundles : { [ uniqueParentId : string ] : Bundle } = {};

                            for ( let count = 0; count < ( nodes[ level ] as DataNode[] ).length; count++ ) {
                                const node = ( nodes as DataNode[][] )[ level ][ count ];

                                let flatNode : PositionedDataNode | undefined;

                                if ( !flatNodes.map( fN => fN.id ).includes( node.id ) ) {
                                    flatNode = {
                                        ...node,
                                        level,
                                        x     : 0,
                                        y     : 0,
                                        width : nodeWidth,
                                        height: nodeHeight
                                    };

                                    flatNodes.push( flatNode );
                                }

                                if ( node.parents ) {
                                    const uniqueParentId = node.parents.sort().join( '-' );

                                    if ( !bundles[ uniqueParentId ] ) {
                                        bundles[ uniqueParentId ] = {
                                            id     : uniqueParentId,
                                            parents: ( [] as number[] ).concat( node.parents ),
                                            level,
                                            span   : level - Math.min( ...node.parents.map( pId => flatNodes.find( fN => fN.id === pId )?.level ?? Infinity ) ),
                                            x      : 0,
                                            y      : 0
                                        };

                                        flatBundles.push( bundles[ uniqueParentId ] );
                                    }

                                    if ( flatNode ) {
                                        flatNode.bundle = bundles[ uniqueParentId ];
                                    }

                                    for ( const pId of node.parents ) {
                                        let id      = `${pId}_${node.id}`,
                                            idCount = 1;

                                        while ( flatLinks.map( fL => fL.id ).includes( id ) ) {
                                            id = `${pId}_${node.id}_${idCount++}`;
                                        }

                                        const cNode                         = cFlatNodes.current.filter( fN => fN.id === node.id )?.[ idCount - 1 ] as DataNode ?? node,
                                              pNode                         = flatNodes.find( fN => fN.id === pId ),
                                              cNodeLabel                    = `<div><span class="yn-secondary">${cNode.itemType}</span><br/><strong>${cNode.label}</strong></div>`,
                                              pNodeLabel                    = `<div><span class="yn-secondary">${pNode?.itemType}</span><br/><strong>${pNode?.label}</strong></div>`,
                                              refLabel                      = `<div><br/>${__( 'YN.base.common.reference.directionMarker' )} ${cNode.reference?.[ 0 ].referenceType} ${__( 'YN.base.common.reference.directionMarker' )}</div>`,
                                              refLabelReversed              = `<div><br/>${__( 'YN.base.common.reference.directionMarkerReversed' )} ${cNode.reference?.[ 0 ].referenceType} ${__( 'YN.base.common.reference.directionMarkerReversed' )}</div>`,
                                              spacer                        = `<div style="width: 1rem;">&nbsp;</div>`,
                                              wrapperStart                  = '<div style="display: flex; align-items: center;">',
                                              wrapperEnd                    = `</div>`,
                                              flatLink : PositionedDataLink = {
                                                  id,
                                                  svgId      : `link_${id}`,
                                                  targetLabel: cNode.reference?.[ 0 ].referenceDirection === 'src'
                                                      ? `${wrapperStart}${pNodeLabel}${spacer}${refLabelReversed}${wrapperEnd}`
                                                      : `${wrapperStart}${pNodeLabel}${spacer}${refLabel}${wrapperEnd}`,
                                                  sourceLabel: cNode.reference?.[ 0 ].referenceDirection === 'src'
                                                      ? `${wrapperStart}${refLabelReversed}${spacer}${cNodeLabel}${wrapperEnd}`
                                                      : `${wrapperStart}${refLabel}${spacer}${cNodeLabel}${wrapperEnd}`,
                                                  fullLabel  : cNode.reference?.[ 0 ].referenceDirection === 'src'
                                                      ? `${wrapperStart}${cNodeLabel}${spacer}${refLabel}${spacer}${pNodeLabel}${wrapperEnd}`
                                                      : `${wrapperStart}${pNodeLabel}${spacer}${refLabel}${spacer}${cNodeLabel}${wrapperEnd}`,
                                                  source     : pNode?.id ?? 0,
                                                  target     : node.id,
                                                  bundle     : bundles[ uniqueParentId ]
                                              };

                                        flatLinks.push( flatLink );

                                        bundles[ uniqueParentId ].links = ( bundles[ uniqueParentId ].links ?? [] ).concat( flatLink );

                                        if ( pNode ) {
                                            pNode.bundles = ( pNode.bundles ?? [] );
                                            if ( !pNode.bundles.includes( bundles[ uniqueParentId ] ) ) {
                                                pNode.bundles.push( bundles[ uniqueParentId ] );
                                            }
                                            pNode.height = Math.max( nodeHeight, ( pNode.bundles!.length - 1 ) * linkPadding + ( 2 * nodeLinkPadding ) );
                                        }
                                    }
                                }
                            }

                            levelBundles[ level ] = Object.values( bundles ).sort( ( b1, b2 ) => {
                                const uniqueReduce  = ( acc : number[], n : number ) => {
                                          if ( !acc.includes( n ) ) {
                                              acc.push( n );
                                          }
                                          return acc;
                                      },
                                      numericSort   = ( n1 : number, n2 : number ) => n1 > n2
                                          ? 1
                                          : n1 < n2
                                              ? -1
                                              : 0,
                                      b1ParentNodes = b1.parents.reduce(
                                          ( acc, pId ) => {
                                              const node = flatNodes.find( fN => fN.id === pId );

                                              if ( node && !acc.includes( node ) ) {
                                                  acc.push( node )
                                              }

                                              return acc;
                                          },
                                          [] as PositionedDataNode[]
                                      ),
                                      b2ParentNodes = b2.parents.reduce(
                                          ( acc, pId ) => {
                                              const node = flatNodes.find( fN => fN.id === pId );

                                              if ( node && !acc.includes( node ) ) {
                                                  acc.push( node )
                                              }

                                              return acc;
                                          },
                                          [] as PositionedDataNode[]
                                      );

                                let retVal = 0;

                                // 1. order by the level of parent nodes
                                if ( retVal === 0 ) {
                                    const b1SourceLevels       = b1ParentNodes.map( pN => pN.level )
                                              .reduce( uniqueReduce, [] )
                                              .sort( numericSort ),
                                          b1SourceLevelsLength = b1SourceLevels.length,
                                          b2SourceLevels       = b2ParentNodes.map( pN => pN.level )
                                              .reduce( uniqueReduce, [] )
                                              .sort( numericSort ),
                                          b2SourceLevelsLength = b2SourceLevels.length;

                                    let b1Level = b1SourceLevels.shift(),
                                        b2Level = b2SourceLevels.shift();

                                    while ( retVal === 0 && b1Level !== undefined && b2Level !== undefined ) {

                                        if ( b1Level > b2Level ) {
                                            retVal = 1;
                                        }
                                        else if ( b1Level < b2Level ) {
                                            retVal = -1;
                                        }

                                        b1Level = b1SourceLevels.shift();
                                        b2Level = b2SourceLevels.shift();
                                    }

                                    if ( retVal === 0 ) {
                                        if ( b1SourceLevelsLength > b2SourceLevelsLength ) {
                                            retVal = 1;
                                        }
                                        else if ( b1SourceLevelsLength < b2SourceLevelsLength ) {
                                            retVal = -1;
                                        }
                                    }
                                }

                                // 2. order by the position of parent nodes
                                if ( retVal === 0 ) {
                                    const b1SourcePositions       = b1ParentNodes.map( pN => ( nodes [ pN.level ] as DataNode[] ).findIndex( n => n.id === pN.id ) )
                                              .reduce( uniqueReduce, [] )
                                              .sort( numericSort ),
                                          b2SourcePositions       = b2ParentNodes.map( pN => ( nodes [ pN.level ] as DataNode[] ).findIndex( n => n.id === pN.id ) )
                                              .reduce( uniqueReduce, [] )
                                              .sort( numericSort ),
                                          b1SourcePositionsLength = b1SourcePositions.length,
                                          b2SourcePositionsLength = b2SourcePositions.length;

                                    let b1Position = b1SourcePositions.shift(),
                                        b2Position = b2SourcePositions.shift();

                                    while ( retVal === 0 && b1Position !== undefined && b2Position !== undefined ) {

                                        if ( b1Position > b2Position ) {
                                            retVal = 1;
                                        }
                                        else if ( b1Position < b2Position ) {
                                            retVal = -1;
                                        }

                                        b1Position = b1SourcePositions.shift();
                                        b2Position = b2SourcePositions.shift();
                                    }

                                    if ( retVal === 0 ) {
                                        if ( b1SourcePositionsLength > b2SourcePositionsLength ) {
                                            retVal = -1;
                                        }
                                        else if ( b1SourcePositionsLength < b1SourcePositionsLength ) {
                                            retVal = 1;
                                        }
                                    }
                                }

                                return retVal;
                            } );

                            ( nodes[ level ] as DataNode[] ).sort( ( n1, n2 ) => {
                                const n1FlatNode  = flatNodes.find( fN => fN.id === n1.id ),
                                      n2FlatNode  = flatNodes.find( fN => fN.id === n2.id ),
                                      n1BundlePos = n1FlatNode ? levelBundles[ level ].findIndex( lB => lB === n1FlatNode.bundle ) : 0,
                                      n2BundlePos = n2FlatNode ? levelBundles[ level ].findIndex( lB => lB === n2FlatNode.bundle ) : 0;

                                // order by bundle position
                                if ( n1BundlePos > n2BundlePos ) {
                                    return 1;
                                }
                                else if ( n1BundlePos < n2BundlePos ) {
                                    return -1;
                                }
                                else {
                                    return 0;
                                }
                            } );
                        }

                        // position nodes and order node bundles
                        for ( let level = 0; level < ( nodes as DataNode[][] ).length; level++ ) {
                            let lastBundlePos = 0;

                            xOffset += ( levelBundles[ level ].length + 2 ) * bundleWidth;

                            for ( let count = 0; count < ( nodes[ level ] as DataNode[] ).length; count++ ) {
                                const node      = ( nodes as DataNode[][] )[ level ][ count ],
                                      flatNode  = flatNodes.find( fN => fN.id === node.id )!,
                                      bundlePos = levelBundles[ level ].findIndex( lB => lB === flatNode.bundle );

                                if ( bundlePos > lastBundlePos ) {
                                    lastBundlePos = bundlePos;
                                    yOffset += nodeHeight + nodePadding;
                                }

                                flatNode.x = xOffset + level * flatNode.width;
                                flatNode.y = yOffset;

                                yOffset += flatNode.height + nodePadding;

                                flatNode.bundles?.sort( ( b1, b2 ) => {
                                    const b1Level = levelBundles.findIndex( b => b.includes( b1 ) ),
                                          b2Level = levelBundles.findIndex( b => b.includes( b2 ) );

                                    if ( b1Level > b2Level ) {
                                        return 1;
                                    }
                                    else if ( b1Level < b2Level ) {
                                        return -1;
                                    }
                                    else {
                                        const b1Order = levelBundles[ b1Level ].findIndex( b => b === b1 ),
                                              b2Order = levelBundles[ b2Level ].findIndex( b => b === b2 );

                                        if ( b1Order > b2Order ) {
                                            return 1;
                                        }
                                        else if ( b1Order < b2Order ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    }
                                } );
                            }
                        }

                        // position bundles
                        for ( let level = 0; level < ( nodes as DataNode[][] ).length; level++ ) {
                            for ( const bundle of levelBundles[ level ] ?? [] ) {
                                bundle.x = Math.max( ...bundle.parents.map( pId => flatNodes.find( fN => fN.id === pId )!.x ) )
                                    + nodeWidth
                                    + ( levelBundles[ level ].length + .5 /*- 1*/ - levelBundles[ level ].indexOf( bundle ) ) * bundleWidth;
                            }
                        }

                        // links
                        const svgLinks = zoomTargetElement
                            ?.select( `#${linksId}` )
                            .selectAll( 'g' )
                            .data( levelBundles )
                            .join( 'g' )
                            .attr( 'class', 'level' )
                            .selectAll( 'g' )
                            .data( d => d3.reverse( d ) )
                            .join( 'g' )
                            .attr( 'class', 'bundle' )
                            .attr( 'id', d => `bundle_${d.id}` )
                            .append( 'g' )
                            .attr( 'stroke-width', 2 )
                            .attr( 'stroke', d => color( Math.max( 0, flatBundles.findIndex( fB => fB === d ) ) as unknown as string ) )
                            .selectAll( 'g' )
                            .data( d => d.links ?? [] );

                        // path-bg
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => `${d.svgId}_bg` )
                            .on( 'click', onPositionedDataLinkClick )
                            .on( 'mouseover', onPositionedDataLinkOver )
                            .on( 'mouseout', onPositionedDataLinkOut )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-none yn-graph-stroke-backgroundColor' )
                            .attr( 'stroke-width', 10 )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'd', d => {
                                const sourceNode = flatNodes.find( fN => fN.id === d.source )!,
                                      targetNode = flatNodes.find( fN => fN.id === d.target )!,
                                      xt         = targetNode.x,
                                      yt         = targetNode.y + targetNode.height / 2,
                                      xs         = sourceNode.x + sourceNode.width,
                                      ys         = sourceNode.y + nodeLinkPadding + ( sourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding,
                                      xb         = d.bundle.x,
                                      lCR1       = Math.min(
                                          linkCornerRadius,
                                          ...d.bundle.links!
                                              .filter( l => l.source === d.source )
                                              .map( l => {
                                                  const lSourceNode = flatNodes.find( fN => fN.id === l.source )!,
                                                        lTargetNode = flatNodes.find( fN => fN.id === l.target )!,
                                                        lyt         = lTargetNode.y + lTargetNode.height / 2,
                                                        lys         = lSourceNode.y + nodeLinkPadding + ( lSourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding;

                                                  if ( ( yt - ys >= 0 && lyt - lys >= 0 ) || ( yt - ys < 0 && lyt - lys < 0 ) ) {
                                                      return Math.abs( lyt - lys ) / 2;
                                                  }
                                                  else {
                                                      return Infinity;
                                                  }
                                              } )
                                      ),
                                      lCR2       = Math.min(
                                          linkCornerRadius,
                                          ...d.bundle.links!
                                              .filter( l => l.target === d.target )
                                              .map( l => {
                                                  const lSourceNode = flatNodes.find( fN => fN.id === l.source )!,
                                                        lTargetNode = flatNodes.find( fN => fN.id === l.target )!,
                                                        lyt         = lTargetNode.y + lTargetNode.height / 2,
                                                        lys         = lSourceNode.y + nodeLinkPadding + ( lSourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding;

                                                  if ( ( yt - ys >= 0 && lyt - lys >= 0 ) || ( yt - ys < 0 && lyt - lys < 0 ) ) {
                                                      return Math.abs( lyt - lys ) / 2;
                                                  }
                                                  else {
                                                      return Infinity;
                                                  }
                                              } )
                                      );

                                if ( ys < yt ) {
                                    return `M${xs} ${ys}
                                        L${xb - lCR1} ${ys}
                                        A${lCR1} ${lCR1} 90 0 1 ${xb} ${ys + lCR1}
                                        L${xb} ${yt - lCR2}
                                        A${lCR2} ${lCR2} 90 0 0 ${xb + lCR2} ${yt}
                                        L${xt} ${yt}`;
                                }
                                else {
                                    return `M${xs} ${ys}
                                        L${xb - lCR1} ${ys}
                                        A${lCR1} ${lCR1} 90 0 0 ${xb} ${ys - lCR1}
                                        L${xb} ${yt + lCR2}
                                        A${lCR2} ${lCR2} 90 0 1 ${xb + lCR2} ${yt}
                                        L${xt} ${yt}`;
                                }
                            } );

                        // path
                        svgLinks?.join( 'path' )
                            .attr( 'id', d => d.svgId )
                            .on( 'click', onPositionedDataLinkClick )
                            .on( 'mouseover', onPositionedDataLinkOver )
                            .on( 'mouseout', onPositionedDataLinkOut )
                            .attr( 'class', 'link yn-graph-fill-none' )
                            .attr( 'cursor', 'pointer' )
                            .attr( 'opacity', '1' )
                            .attr( 'd', d => {
                                const sourceNode = flatNodes.find( fN => fN.id === d.source )!,
                                      targetNode = flatNodes.find( fN => fN.id === d.target )!,
                                      xt         = targetNode.x,
                                      yt         = targetNode.y + targetNode.height / 2,
                                      xs         = sourceNode.x + sourceNode.width,
                                      ys         = sourceNode.y + nodeLinkPadding + ( sourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding,
                                      xb         = d.bundle.x,
                                      lCR1       = Math.min(
                                          linkCornerRadius,
                                          ...d.bundle.links!
                                              .filter( l => l.source === d.source )
                                              .map( l => {
                                                  const lSourceNode = flatNodes.find( fN => fN.id === l.source )!,
                                                        lTargetNode = flatNodes.find( fN => fN.id === l.target )!,
                                                        lyt         = lTargetNode.y + lTargetNode.height / 2,
                                                        lys         = lSourceNode.y + nodeLinkPadding + ( lSourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding;

                                                  if ( ( yt - ys >= 0 && lyt - lys >= 0 ) || ( yt - ys < 0 && lyt - lys < 0 ) ) {
                                                      return Math.abs( lyt - lys ) / 2;
                                                  }
                                                  else {
                                                      return Infinity;
                                                  }
                                              } )
                                      ),
                                      lCR2       = Math.min(
                                          linkCornerRadius,
                                          ...d.bundle.links!
                                              .filter( l => l.target === d.target )
                                              .map( l => {
                                                  const lSourceNode = flatNodes.find( fN => fN.id === l.source )!,
                                                        lTargetNode = flatNodes.find( fN => fN.id === l.target )!,
                                                        lyt         = lTargetNode.y + lTargetNode.height / 2,
                                                        lys         = lSourceNode.y + nodeLinkPadding + ( lSourceNode.bundles?.findIndex( fB => fB === d.bundle ) ?? 0 ) * linkPadding;

                                                  if ( ( yt - ys >= 0 && lyt - lys >= 0 ) || ( yt - ys < 0 && lyt - lys < 0 ) ) {
                                                      return Math.abs( lyt - lys ) / 2;
                                                  }
                                                  else {
                                                      return Infinity;
                                                  }
                                              } )
                                      );

                                if ( ys < yt ) {
                                    return `M${xs} ${ys}
                                        L${xb - lCR1} ${ys}
                                        A${lCR1} ${lCR1} 90 0 1 ${xb} ${ys + lCR1}
                                        L${xb} ${yt - lCR2}
                                        A${lCR2} ${lCR2} 90 0 0 ${xb + lCR2} ${yt}
                                        L${xt} ${yt}`;
                                }
                                else {
                                    return `M${xs} ${ys}
                                        L${xb - lCR1} ${ys}
                                        A${lCR1} ${lCR1} 90 0 0 ${xb} ${ys - lCR1}
                                        L${xb} ${yt + lCR2}
                                        A${lCR2} ${lCR2} 90 0 1 ${xb + lCR2} ${yt}
                                        L${xt} ${yt}`;
                                }
                            } );

                        // nodes
                        const svgNodes = zoomTargetElement
                            ?.select( `#${nodesId}` )
                            .selectAll( 'g' )
                            .data( flatNodes )
                            .join( 'g' )
                            .attr( 'id', d => d.svgId )
                            .attr( 'transform', d => `translate(${d.x},${d.y})` )
                            .attr( 'opacity', '1' )
                            .attr( 'cursor', 'pointer' ) as d3.Selection<SVGGElement, PositionedDataNode, d3.BaseType, unknown> | undefined;

                        // handler
                        svgNodes?.on( 'click', onDataNodeClick )
                            .on( 'dblclick', onDataDoubleClick )
                            .on( 'mouseover', onPositionedDataNodeOver )
                            .on( 'mouseout', onPositionedDataNodeOut );

                        // icon
                        svgNodes?.append( 'foreignObject' )
                            .attr( 'id', d => `${d.svgId}_icon` )
                            .attr( 'x', 12 )
                            .attr( 'y', 6 )
                            .attr( 'width', 24 )
                            .attr( 'height', 24 )
                            .append( 'xhtml:div' )
                            .attr( 'xmlns', 'http://www.w3.org/1999/xhtml' )
                            .attr( 'class', d => `yn-graph-node-icon ${d.icon}` );

                        // label
                        svgNodes?.append( 'svg' )
                            .attr( 'x', 42 )
                            .attr( 'y', 0 )
                            .attr( 'width', d => d.width - 52 )
                            .attr( 'height', 30 )
                            .append( 'text' )
                            .attr( 'id', d => `${d.svgId}_label` )
                            .attr( 'class', 'yn-graph-fill-textColor' )
                            .attr( 'opacity', '1' )
                            .text( d => d.label )
                            .attr( 'x', 0 )
                            .attr( 'y', 22 );

                        // background
                        svgNodes?.append( 'rect' )
                            .lower()
                            .attr( 'id', d => `${d.svgId}_background` )
                            .attr( 'x', 0 )
                            .attr( 'y', 0 )
                            .attr( 'width', d => d.width )
                            .attr( 'height', d => d.height )
                            .attr( 'rx', 6 )
                            .attr( 'class', 'yn-graph-node' )
                            .attr( 'style', d => colorItemTypes && d.color
                                ? `fill: ${d.color}`
                                : null
                            )
                            .attr( 'stroke-width', 2 )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.svgId}_color_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-backgroundColor' )
                            .attr( 'style', null )
                            .clone( true )
                            .lower()
                            .attr( 'id', d => `${d.svgId}_shadow_background` )
                            .attr( 'class', 'yn-svg-no-transition yn-graph-fill-dropShadowColor yn-graph-stroke-dropShadowColor' )
                            .attr( 'stroke-width', 3 )
                            .attr( 'filter', `url(#${shadowFilterId})` );
                    }

                    fitSvgGraphToCenter();
                    setTimeout(
                        () => {
                            fitSvgGraphToCenter();
                            zoomTargetElement?.attr( 'opacity', 1 );
                        },
                        1
                    );
                }
            }
        },
        [
            colorItemTypes,
            elementId,
            graphType,
            hasRendered.current,
            iconNodeOnly,
            isConfigLoaded,
            isDataLoaded,
            layout,
            links,
            nodes,
            onDataLinkClick,
            onDataLinkOut,
            onDataLinkOver,
            onDataNodeClick,
            onDataNodeOut,
            onDataNodeOver,
            onMetaLinkClick,
            onMetaLinkOut,
            onMetaLinkOver,
            onMetaNodeClick,
            onMetaNodeDblClick,
            onMetaNodeDrag,
            onMetaNodeDragEnd,
            onMetaNodeDragStart,
            onMetaNodeOut,
            onMetaNodeOver,
            onPositionedDataLinkClick,
            onPositionedDataLinkOut,
            onPositionedDataLinkOver,
            onPositionedDataNodeOut,
            onPositionedDataNodeOver,
            type
        ]
    );

    // update height and width
    useEffect(
        () => {
            if ( graphSvg.current ) {
                d3.select( `#${elementId}_svg` )
                    .attr( 'viewBox', [ 0, 0, graphWidth, graphHeight ] )
                    .attr( 'height', graphHeight )
                    .attr( 'width', graphWidth );

                const zoom = d3.zoom<SVGGElement, unknown>()
                    .extent( [ [ 0, 0 ], [ graphWidth, graphHeight ] ] )
                    .scaleExtent( [ .1, 10 ] )
                    .on( 'zoom', onZoom );

                // remove current zoom handler
                graphSvg.current
                    .on( '.zoom', null );
                // add new zoom handler
                graphSvg.current
                    .call( zoom )
                    .on( 'dblclick.zoom', null );

                // restore last zoom state if
                if ( svgGraphTransform ) {
                    const transform = new d3.ZoomTransform(
                        svgGraphTransform.k ?? 1,
                        svgGraphTransform.x ?? 0,
                        svgGraphTransform.y ?? 0
                    );

                    graphSvg.current
                        .select( `#${zoomTargetElementId}` )
                        .attr( 'transform', transform.toString() );

                    graphSvg.current
                        .call( zoom.transform, transform );
                }
            }
        },
        [
            graphHeight,
            graphWidth,
            graphSvg.current
        ]
    );

    // update selected nodes
    useEffect(
        () => {
            selectNodes( selectedNodes ?? [] );
        },
        [
            selectedNodes
        ]
    );

    // update show flags
    useEffect(
        () => {
            graphSvg.current
                ?.select( `#${linksId}` )
                .selectAll( 'text' )
                .filter( ( d, i, nodeList ) => {
                    const textElementId = ( nodeList[ i ] as SVGElement | undefined )?.id;

                    return textElementId === ( d as Link ).svgId + '_label';
                } )
                .attr( 'visibility', showLinkNames ? 'visible' : 'hidden' );

            graphSvg.current
                ?.select( `#${linksId}` )
                .selectAll( 'text' )
                .filter( ( d, i, nodeList ) => {
                    const textElementId = ( nodeList[ i ] as SVGElement | undefined )?.id;

                    return textElementId === ( d as Link ).svgId + '_cardinality_src' || textElementId === ( d as Link ).svgId + '_cardinality_dest';
                } )
                .attr( 'visibility', showLinkCardinality ? 'visible' : 'hidden' );
        },
        [
            showLinkCardinality,
            showLinkNames
        ]
    );

    // update observed element of resizeObserver
    useEffect(
        () => {
            if ( observedGraphParentElement.current ) {
                resizeObserver.unobserve( observedGraphParentElement.current );
            }
            if ( graphParentElement ) {
                observedGraphParentElement.current = graphParentElement;
                resizeObserver.observe( graphParentElement );
            }
        },
        [
            graphParentElement
        ]
    );

    return useMemo(
        () => {
            return <>
                {showSimulationParametersDialog}
                {graph}
                {filtersMenu}
                {optionsMenu}
                {itemTypeVisibilityAndColorLegend}
                {statisticsOverlay}
                {tooltip}
            </>;
        },
        [
            filtersMenu,
            graph,
            itemTypeVisibilityAndColorLegend,
            optionsMenu,
            statisticsOverlay,
            showSimulationParametersDialog,
            tooltip
        ]
    );
};
Graph.defaultProps = defaultProps;
Graph.displayName = 'Graph';

export default Graph;
